import { useState, useRef, useEffect } from 'react';
import {
    handleDeerFlowChat,
    cancelDeerFlowChat,
    handleDeerFlowHistory as apiHandleDeerFlowHistory,
    handleDeerFlowStop,
    handleHistoryRecord
} from '@/api/grt/ai';

import { setUserStoppedState, checkUserStoppedState, clearStoppedState } from './useReportUtils';
import { third_json_handler, contentType, messageType } from '@/utils/Page/constant';
import { Allow, parse } from 'partial-json';

// 清除会话的暂停标识（会话结束时调用）
const clearSessionPauseFlag = (sessionId) => {
    if (!sessionId) return;

    try {
        const historyKey = 'grt_history_sessions';
        const existingSessions = JSON.parse(localStorage.getItem(historyKey) || '[]');

        // 查找对应的会话
        const sessionIndex = existingSessions.findIndex((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));

        if (sessionIndex !== -1 && typeof existingSessions[sessionIndex] === 'object') {
            // 设置暂停标识为false（而不是删除）
            existingSessions[sessionIndex].canPause = false;
            // 报告结束时，设置按钮不可点击
            existingSessions[sessionIndex].isClickable = false;
            localStorage.setItem(historyKey, JSON.stringify(existingSessions));
        }
    } catch (error) {
        console.error('清除会话暂停标识失败:', error);
    }
};

// 获取会话的暂停标识
const getSessionPauseFlag = (sessionId) => {
    if (!sessionId) return false;

    try {
        const historyKey = 'grt_history_sessions';
        const existingSessions = JSON.parse(localStorage.getItem(historyKey) || '[]');

        // 查找对应的会话
        const session = existingSessions.find((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));

        if (session && typeof session === 'object') {
            const canPause = session.canPause;
            return canPause;
        }
    } catch (error) {
        console.error('获取会话暂停标识失败:', error);
        return false;
    }
};

const useDeerFlow = () => {
    // DeerFlow相关状态
    const [editPlanMode, setEditPlanMode] = useState(false); // 修改计划模式状态
    const [planButtonsDisabled, setPlanButtonsDisabled] = useState(false); // 计划按钮禁用状态
    const lastThreadIdRef = useRef(null); // 存储上一次使用的thread_id

    // 历史记录轮询管理
    const historyPollingTimers = useRef(new Map()); // 存储每个会话的轮询定时器
    const delayedStartTimers = useRef(new Map()); // 存储延迟启动定时器

    // 🔥 新增：滚动到底部的功能
    const scrollToBottomWithAnimation = (listRef) => {
        if (listRef.current && listRef.current.nativeElement) {
            const container = listRef.current.nativeElement;

            // 使用 requestAnimationFrame 确保 DOM 更新完成
            requestAnimationFrame(() => {
                // 直接设置到底部
                // container.scrollTop = container.scrollHeight;

                // 备用方法确保精确到底部
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'instant'
                });
            });
        }
    };

    // 🔥 修改：创建滚动监听功能的参数，而不是直接返回 useEffect
    const getScrollToBottomParams = (sessionMap, currentSessionMapIdRef, listRef) => {
        return {
            dependencies: [sessionMap, currentSessionMapIdRef?.current],
            callback: () => {
                // 获取当前会话ID
                const currentSessionId = currentSessionMapIdRef?.current;
                if (!currentSessionId) return;

                // 获取当前会话的消息数据
                const currentSessionData = sessionMap?.[currentSessionId];
                const currentMessages = currentSessionData?.messages;

                // 只有当会话有消息数据时才设置滚动位置
                if (currentMessages && currentMessages.length > 0) {
                    // 获取容器元素
                    const container = listRef.current?.nativeElement;
                    if (!container) return;

                    // 判断用户是否在底部附近（例如距离底部小于100px）
                    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

                    // 只有当用户在底部附近时才自动滚动到底部
                    if (isNearBottom) {
                        scrollToBottomWithAnimation(listRef);
                    }
                }
            }
        };
    };

    // 启动历史记录轮询
    const startHistoryPolling = (
        session,
        { formatHistoryRecord, changeSessionMapItem, changeOriginMsgMapRef, originMsgMapRef, currentSessionMapIdRef, skillListRef }
    ) => {
        const sessionId = session?.session_id;
        if (!sessionId) {
            return;
        }

        // 🔥 关键修复：检查是否已经有handleDeerFlowChatWithUI的轮询在运行
        if (window.activeDeerFlowPollings && window.activeDeerFlowPollings.has(sessionId)) {
            return;
        }

        // 如果已经有startHistoryPolling轮询在运行，先清除
        if (historyPollingTimers.current.has(sessionId)) {
            clearTimeout(historyPollingTimers.current.get(sessionId));
        }

        let pollCount = 0;
        const maxPollCount = 70; // 最多轮询70次
        const pollInterval = 5000; // 每5秒轮询一次

        const pollFunction = async () => {
            pollCount++;

            // 检查是否还是当前会话
            if (currentSessionMapIdRef.current !== sessionId) {
                historyPollingTimers.current.delete(sessionId);
                return;
            }

            // 检查是否超过最大轮询次数
            if (pollCount > maxPollCount) {
                historyPollingTimers.current.delete(sessionId);

                // 轮询超时时也要更新本地存储
                try {
                    const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                    const sessionIndex = historySessions.findIndex((item) =>
                        typeof item === 'object' ? item.sessionId === sessionId : item === sessionId
                    );
                    if (sessionIndex !== -1 && typeof historySessions[sessionIndex] === 'object') {
                        historySessions[sessionIndex].canPause = false;
                        historySessions[sessionIndex].isClickable = false;
                        localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));
                    }
                } catch (error) {
                    console.error('🔥 轮询超时时更新本地存储失败:', error);
                }

                return;
            }

            try {
                let res;
                if (isDeerFlowType(session.skill_id, skillListRef)) {
                    res = await apiHandleDeerFlowHistory({ thread_id: sessionId });
                } else {
                    res = await handleHistoryRecord({ session_id: sessionId });
                }

                if (res && (res.code === 200 || res.success)) {
                    const messages = isDeerFlowType(session.skill_id, skillListRef) ? res.data.messages : res.data;
                    const isDeerFlow = isDeerFlowType(session.skill_id, skillListRef);

                    // 对于DeerFlow，需要检查任务状态而不是仅仅检查AI回复
                    let shouldStopPolling = false;

                    if (isDeerFlow) {
                        // DeerFlow: 检查任务状态和消息状态
                        const taskStatusMap = res.data?.task_status_map || {};
                        const currentTaskId = res.data?.current_task_id;

                        // 🔥 获取task_status_map中的最后一个task_id
                        const taskIds = Object.keys(taskStatusMap);
                        const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;

                        // 同时检查消息的实际状态
                        const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
                        const hasLoadingMessage = messages && messages.some((msg) => msg.status === 'loading');

                        // 综合判断是否应该停止轮询
                        // 获取当前任务的具体状态
                        const currentTaskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

                        // 🔥 检查本地存储，使用最后一个task_id来检查停止状态
                        const isUserStopped = lastTaskId ? checkUserStoppedState(lastTaskId) : false;

                        // 🔥 添加详细的停止状态检查日志
                        const stoppedReportsRaw = localStorage.getItem('deerflow_stopped_reports');

                        // 🔥 关键修复：如果任务状态是0（完成）或-1（停止）或用户主动停止，直接停止轮询
                        // 不需要检查其他条件，因为这些状态就表示任务已经结束
                        const taskNotContinuing = currentTaskStatus === 0 || currentTaskStatus === -1 || isUserStopped;
                        const noLoadingMessages = !hasLoadingMessage;
                        const lastMessageNotUser = lastMessage && lastMessage.role !== 'user';

                        // 检查是否有reporter消息（最终报告）
                        // 只有当存在reporter消息时，才认为研究报告任务完成
                        const hasReporterMessage = (() => {
                            if (!isDeerFlow) return false;

                            // 查找是否有reporter消息（最终报告消息）
                            const reporterMessages = messages.filter((msg) => msg.name === 'reporter');
                            return reporterMessages.length > 0 && reporterMessages.some((msg) => msg.content && msg.content.trim() !== '');
                        })();

                        // 修改判断逻辑：任务状态为0或-1时，无论其他条件如何都应该停止轮询
                        if (taskNotContinuing || hasReporterMessage) {
                            shouldStopPolling = true;
                        }
                    } else {
                        // 非DeerFlow: 检查最后一条消息是否为AI消息
                        const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
                        const hasAIReply = lastMessage && lastMessage.role !== 'user';
                        shouldStopPolling = hasAIReply;
                    }

                    if (shouldStopPolling) {
                        // 先删除当前定时器
                        historyPollingTimers.current.delete(sessionId);

                        // 直接处理历史消息，但不触发新的轮询检测
                        let { showMsg, allMsg } = await formatHistoryRecord(messages, session);

                        const sessionState = {
                            messages: showMsg,
                            loading: false, // 已获取到AI回复，不再加载中
                            answering: false
                        };

                        // 重要：同时更新originMsgMapRef，根据任务状态设置正确的status
                        let finalStatus = null;
                        if (isDeerFlow) {
                            // 重新获取任务状态
                            const taskStatusMap = res.data?.task_status_map || {};
                            const currentTaskId = res.data?.current_task_id;
                            const taskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

                            // 🔥 获取task_status_map中的最后一个task_id
                            const taskIds = Object.keys(taskStatusMap);
                            const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;

                            // 🔥 关键修复：优先检查本地停止状态，使用最后一个task_id
                            // 如果用户手动停止了任务，即使后端返回状态是2，也应该标记为-1
                            const isUserStopped = lastTaskId ? checkUserStoppedState(lastTaskId) : false;

                            if (isUserStopped || taskStatus === -1) {
                                finalStatus = -1; // 任务停止
                            } else if (taskStatus === 0) {
                                finalStatus = null; // 任务完成
                            }
                        }

                        changeOriginMsgMapRef({
                            [session?.session_id]: {
                                ...originMsgMapRef.current[session?.session_id],
                                messages: allMsg,
                                status: finalStatus
                            }
                        });

                        changeSessionMapItem(session?.session_id, sessionState, allMsg);

                        // 派发报告完成事件给侧边栏
                        if (isDeerFlow) {
                            // 从原始messages中提取最新的报告内容，因为allMsg中的researcher消息被标记为isHidden
                            const latestReportContent = messages
                                .filter((msg) => msg.role === 'assistant' && (msg.name === 'researcher' || msg.name === 'coder'))
                                .pop();

                            if (latestReportContent) {
                                // 从内容中提取标题
                                const titleMatch = latestReportContent.content.match(/^#\s*(.+)/m);
                                const extractedTitle = titleMatch ? titleMatch[1].trim() : '研究报告';

                                // 根据任务状态设置正确的事件状态
                                const taskStatusMap = res.data?.task_status_map || {};
                                const currentTaskId = res.data?.current_task_id;
                                const taskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

                                // 🔥 获取task_status_map中的最后一个task_id
                                const taskIds = Object.keys(taskStatusMap);
                                const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;

                                // 🔥 检查本地停止状态，使用最后一个task_id
                                const isUserStoppedForEvent = lastTaskId ? checkUserStoppedState(lastTaskId) : false;
                                const eventStatus = taskStatus === -1 || isUserStoppedForEvent ? 'stopped' : 'completed';

                                const eventDetail = {
                                    reportId: sessionId,
                                    uniqueReportId: sessionId,
                                    content: latestReportContent.content,
                                    status: eventStatus,
                                    title: extractedTitle,
                                    isFinal: true,
                                    timestamp: Date.now()
                                };

                                window.dispatchEvent(
                                    new CustomEvent('report-content-update', {
                                        detail: eventDetail
                                    })
                                );
                            }

                            // 🔥 更新 deerflow_reports_data 本地存储（完成状态）
                            if (latestReportContent) {
                                try {
                                    // 从消息中提取活动内容（researcher和coder消息）
                                    const activityMessages = messages.filter(
                                        (msg) => msg.role === 'assistant' && (msg.name === 'researcher' || msg.name === 'coder')
                                    );
                                    const activityContent = activityMessages.map((msg) => msg.content).join('\n\n');

                                    // 从内容中提取标题
                                    const titleMatch = latestReportContent.content.match(/^#\s*(.+)/m);
                                    const extractedTitle = titleMatch ? titleMatch[1].trim() : '研究报告';

                                    // 导入 saveReportToLocalStorage 函数
                                    const { saveReportToLocalStorage } = await import('./useReportUtils');

                                    // 保存到本地存储（标记为已完成）
                                    saveReportToLocalStorage({
                                        threadId: sessionId,
                                        reportId: latestReportContent.task_id || sessionId,
                                        uniqueReportId: latestReportContent.task_id || sessionId,
                                        activityContent: activityContent,
                                        reportContent: latestReportContent.content,
                                        title: extractedTitle,
                                        isFinal: true // 标记为已完成
                                    });
                                } catch (error) {
                                    console.error('🔥 更新 deerflow_reports_data（完成）失败:', error);
                                }
                            }
                        }

                        // 更新本地存储，将canPause和isClickable设置为false
                        try {
                            const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                            const sessionIndex = historySessions.findIndex((item) =>
                                typeof item === 'object' ? item.sessionId === sessionId : item === sessionId
                            );
                            if (sessionIndex !== -1 && typeof historySessions[sessionIndex] === 'object') {
                                historySessions[sessionIndex].canPause = false;
                                historySessions[sessionIndex].isClickable = false;
                                localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));
                            }
                        } catch (error) {
                            console.error('🔥 轮询完成时更新本地存储失败:', error);
                        }

                        return;
                    } else {
                        // 如果不需要停止轮询，也要更新消息显示
                        let { showMsg, allMsg } = await formatHistoryRecord(messages, session);

                        // 关键修复：轮询继续时也要派发进行中事件给侧边栏
                        if (isDeerFlow) {
                            // 从原始messages中提取最新的报告内容，因为allMsg中的researcher消息被标记为isHidden
                            const latestReportContent = messages
                                .filter((msg) => msg.role === 'assistant' && (msg.name === 'researcher' || msg.name === 'coder'))
                                .pop();

                            if (latestReportContent) {
                                // 从内容中提取标题
                                const titleMatch = latestReportContent.content.match(/^#\s*(.+)/m);
                                const extractedTitle = titleMatch ? titleMatch[1].trim() : '研究报告';

                                const eventDetail = {
                                    reportId: sessionId,
                                    uniqueReportId: sessionId,
                                    content: latestReportContent.content,
                                    status: 'in_progress',
                                    title: extractedTitle,
                                    isFinal: false, // 还在进行中
                                    timestamp: Date.now()
                                };

                                window.dispatchEvent(
                                    new CustomEvent('report-content-update', {
                                        detail: eventDetail
                                    })
                                );
                            }

                            // 🔥 更新 deerflow_reports_data 本地存储（进行中状态）
                            if (latestReportContent) {
                                try {
                                    // 从消息中提取活动内容（researcher和coder消息）
                                    const activityMessages = messages.filter(
                                        (msg) => msg.role === 'assistant' && (msg.name === 'researcher' || msg.name === 'coder')
                                    );
                                    const activityContent = activityMessages.map((msg) => msg.content).join('\n\n');

                                    // 从内容中提取标题
                                    const titleMatch = latestReportContent.content.match(/^#\s*(.+)/m);
                                    const extractedTitle = titleMatch ? titleMatch[1].trim() : '研究报告';

                                    // 导入 saveReportToLocalStorage 函数
                                    const { saveReportToLocalStorage } = await import('./useReportUtils');

                                    // 保存到本地存储（标记为进行中）
                                    saveReportToLocalStorage({
                                        threadId: sessionId,
                                        reportId: latestReportContent.task_id || sessionId,
                                        uniqueReportId: latestReportContent.task_id || sessionId,
                                        activityContent: activityContent,
                                        reportContent: latestReportContent.content,
                                        title: extractedTitle,
                                        isFinal: false // 标记为进行中
                                    });
                                } catch (error) {
                                    console.error('🔥 更新 deerflow_reports_data（进行中）失败:', error);
                                }
                            }
                        }

                        // 🔥 关键修复：只有任务状态确实是进行中时才设置loading为true
                        // 获取当前任务状态来判断是否应该保持loading
                        const taskStatusMap = res.data?.task_status_map || {};
                        const currentTaskId = res.data?.current_task_id;
                        const currentTaskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

                        // 🔥 获取task_status_map中的最后一个task_id
                        const taskIds = Object.keys(taskStatusMap);
                        const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;

                        // 检查是否有loading状态的消息
                        const hasLoadingMessageInElse = messages && messages.some((msg) => msg.status === 'loading');

                        // 🔥 检查本地是否有停止标记，使用最后一个task_id
                        const isUserStoppedInElse = lastTaskId ? checkUserStoppedState(lastTaskId) : false;

                        // 只有任务状态为2（继续）或有loading消息时，且用户没有手动停止，才保持loading状态
                        const shouldKeepLoading = !isUserStoppedInElse && (currentTaskStatus === 2 || hasLoadingMessageInElse);

                        const sessionState = {
                            messages: showMsg,
                            loading: shouldKeepLoading, // 根据实际任务状态决定loading
                            answering: false
                        };

                        // 🔥 更新originMsgMapRef，根据用户停止状态设置正确的status
                        const pollingStatus = isUserStoppedInElse ? -1 : 0;
                        changeOriginMsgMapRef({
                            [session?.session_id]: {
                                ...originMsgMapRef.current[session?.session_id],
                                messages: allMsg,
                                status: pollingStatus // 用户停止时为-1，否则为0（继续轮询）
                            }
                        });

                        changeSessionMapItem(session?.session_id, sessionState, allMsg);

                        // 🔥 关键修复：如果任务已经完成或停止，或用户手动停止了，不应该继续轮询
                        if (!shouldKeepLoading && (currentTaskStatus === 0 || currentTaskStatus === -1 || isUserStoppedInElse)) {
                            historyPollingTimers.current.delete(sessionId);

                            // 如果是用户停止的，更新status为-1
                            if (isUserStoppedInElse) {
                                changeOriginMsgMapRef({
                                    [session?.session_id]: {
                                        ...originMsgMapRef.current[session?.session_id],
                                        messages: allMsg,
                                        status: -1
                                    }
                                });
                            }

                            return;
                        }
                    }
                }
            } catch (error) {
                console.error(`🔄 轮询第${pollCount}次失败:`, error);
            }

            // 继续下一次轮询
            const timer = setTimeout(pollFunction, pollInterval);
            historyPollingTimers.current.set(sessionId, timer);
        };

        // 开始轮询
        const timer = setTimeout(pollFunction, pollInterval);
        historyPollingTimers.current.set(sessionId, timer);
    };

    // 停止指定会话的轮询
    const stopHistoryPolling = (sessionId, shouldUpdateCanPause = true) => {
        if (!sessionId) return;

        // 支持不同类型的 sessionId（string/number），尝试多种 key 以确保能找到计时器
        const possibleKeys = [sessionId];
        try {
            const asString = String(sessionId);
            if (!possibleKeys.includes(asString)) possibleKeys.push(asString);
        } catch (e) {}
        try {
            const asNumber = Number(sessionId);
            if (!Number.isNaN(asNumber) && !possibleKeys.includes(asNumber)) possibleKeys.push(asNumber);
        } catch (e) {}

        // 清除轮询定时器（尝试所有可能的 key）
        possibleKeys.forEach((key) => {
            if (historyPollingTimers.current.has(key)) {
                try {
                    clearTimeout(historyPollingTimers.current.get(key));
                } catch (e) {}
                historyPollingTimers.current.delete(key);
            }
        });

        // 同时清除延迟启动定时器（尝试所有可能的 key）
        possibleKeys.forEach((key) => {
            if (delayedStartTimers.current.has(key)) {
                try {
                    clearTimeout(delayedStartTimers.current.get(key));
                } catch (e) {}
                delayedStartTimers.current.delete(key);
            }
        });

        // 只在需要时才更新本地存储的canPause状态
        // 切换会话时不更新canPause，只有真正完成或超时时才更新
        if (shouldUpdateCanPause) {
            try {
                const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                const sessionIndex = historySessions.findIndex((item) =>
                    typeof item === 'object' ? item.sessionId === sessionId : item === sessionId
                );
                if (sessionIndex !== -1 && typeof historySessions[sessionIndex] === 'object') {
                    historySessions[sessionIndex].canPause = false;
                    localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));
                }
            } catch (error) {
                console.error('🔥 更新本地存储失败:', error);
            }
        }
    };

    // 清理所有轮询定时器
    const clearAllPollingTimers = () => {
        // 清理轮询定时器
        historyPollingTimers.current.forEach((timer) => {
            clearTimeout(timer);
        });
        historyPollingTimers.current.clear();

        // 清理延迟启动定时器
        delayedStartTimers.current.forEach((timer) => {
            clearTimeout(timer);
        });
        delayedStartTimers.current.clear();
    };

    // 组件卸载时清理所有轮询
    useEffect(() => {
        return () => {
            clearAllPollingTimers();
        };
    }, []);

    // 判断是否为DeerFlow类型
    const isDeerFlowType = (skillId, skillListRef) => {
        if (!skillListRef?.current) return false;
        // 🔥 修复：支持字符串和数字类型的skillId比较

        const temSkill = skillListRef.current.find((ele) => String(ele.ability_id) === String(skillId));
        return temSkill?.third_json?.handler === third_json_handler.deerflow;
    };

    // 切换修改计划模式
    const toggleEditPlanMode = (setContent) => {
        setEditPlanMode((prevState) => {
            const newState = !prevState;

            // 如果进入编辑模式，不自动插入提示文字，仅聚焦输入框以便用户直接输入
            if (newState && setContent) {
                // 添加一个延时，确保状态更新后再设置输入框内容/聚焦
                setTimeout(() => {
                    // 保持输入框为空，避免插入不需要的提示文本
                    setContent('');

                    // 尝试获取输入框并聚焦
                    try {
                        const inputElement = document.querySelector('.web-sender-textarea');
                        if (inputElement) {
                            inputElement.focus();
                            // 只有在输入框已有内容时才选中全部文本
                            if (inputElement.value) {
                                inputElement.select();
                            }
                        }
                    } catch (e) {
                        console.error('聚焦输入框失败:', e);
                    }
                }, 100);
            }

            return newState;
        });
    };

    // 重置所有研究计划的编辑状态
    const resetEditPlanMode = () => {
        setEditPlanMode(false);
        // 退出修改计划模式时，重置按钮状态
        setPlanButtonsDisabled(false);
    };

    // DeerFlow特殊的消息更新处理
    const handleDeerFlowMessageUpdate = (
        currentMsg,
        processing,
        msgList,
        localsession_key,
        {
            currentSessionMapIdRef,
            sessionListRef,
            sessionMapRef,
            formatSessionList,
            linkToSession,
            delayedStartTimers,
            changeSessionMapItem,
            isInAnswerRef,
            side,
            effectiveSessionId
        }
    ) => {
        // DeerFlow 新会话处理
        if (!sessionMapRef.current?.[effectiveSessionId]) {
            let curList = sessionListRef.current ? sessionListRef.current : [];
            let localList = curList[0];
            if (effectiveSessionId) {
                // DeerFlow 新会话：使用后端返回的 thread_id 更新会话列表
                curList[0] = {
                    ...localList,
                    status: currentMsg?.status,
                    session_name: currentMsg.question,
                    session_id: effectiveSessionId,
                    // 保留原有的 skill_id，如果没有则从请求参数中获取
                    skill_id: localList?.skill_id || currentMsg?.skill_id
                };
            }

            let data = [...curList];
            formatSessionList(data);
        }

        // 新会话结束后处理
        if (processing === false) {
            changeSessionMapItem(effectiveSessionId, { loading: false });
            isInAnswerRef.current = false;

            // DeerFlow 消息完成后，自动获取历史记录以更新侧边栏
            if (effectiveSessionId) {
                // 清除可能存在的延迟启动定时器
                if (delayedStartTimers.current.has(effectiveSessionId)) {
                    clearTimeout(delayedStartTimers.current.get(effectiveSessionId));
                    delayedStartTimers.current.delete(effectiveSessionId);
                }

                // DeerFlow消息完成后不再启动useChat的轮询
                const delayTimer = setTimeout(async () => {
                    // 清除自己的引用
                    delayedStartTimers.current.delete(effectiveSessionId);
                }, 1000);

                // 存储延迟定时器
                delayedStartTimers.current.set(effectiveSessionId, delayTimer);
            }

            // 消息处理完成后，只有不在修改计划模式时才重置按钮禁用状态
            if (!editPlanMode) {
                setTimeout(() => {
                    setPlanButtonsDisabled(false);
                }, 300);
            }
        }
    };

    // 创建DeerFlow请求
    const createDeerFlowRequest = (
        nextId,
        msgList,
        requestChatParams,
        isNewSession,
        {
            updateMsgListByCurmsg,
            handleMessages,
            changeSessionMapItem,
            changeOriginMsgMapRef,
            originMsgMapRef,
            isInAnswerRef,
            currentSessionMapIdRef,
            checkIsLogin
        }
    ) => {
        // 获取发起请求时的会话ID，用于后续验证
        const requestSessionId = currentSessionMapIdRef?.current;

        handleDeerFlowChatWithUI({
            nextId,
            msgList,
            requestChatParams,
            editPlanMode,
            lastThreadIdRef,
            updateMsgListByCurmsg,
            handleMessages,
            changeSessionMapItem,
            changeOriginMsgMapRef,
            originMsgMapRef,
            isInAnswerRef,
            currentSessionMapIdRef,
            isNewSession,
            checkIsLogin,
            requestSessionId,
            historyPollingTimers // 🔥 新增：传递轮询定时器管理器，避免重复轮询
        });
    };

    // DeerFlow会话切换处理
    const handleDeerFlowSessionSwitch = (
        previousSessionId,
        currentSession,
        { stopHistoryPolling, changeSessionMapItem, changeOriginMsgMapRef, originMsgMapRef }
    ) => {
        if (previousSessionId && isDeerFlowType(currentSession.skill_id)) {
            // 切换会话时不更新canPause状态，保持原有状态
            stopHistoryPolling(previousSessionId, false);

            // 只有当之前会话是DeerFlow类型时，才停止其相关请求
            if (isDeerFlowType(currentSession.skill_id)) {
                // 取消API请求
                cancelDeerFlowChat();

                // 更新之前会话的状态为停止
                changeSessionMapItem(previousSessionId, {
                    loading: false,
                    answering: false
                });
            }
        }

        // 触发会话切换事件，通知报告侧边栏更新标题
        if (isDeerFlowType(currentSession.skill_id)) {
            window.dispatchEvent(
                new CustomEvent('session-switched', {
                    detail: {
                        sessionId: currentSession.session_id,
                        skillId: currentSession.skill_id
                    }
                })
            );
        }
    };

    // DeerFlow消息发送前处理
    const handleDeerFlowBeforeSubmit = (
        requestChatParams,
        isNewSession,
        { currentSessionMapIdRef, sessionListRef, formatSessionList, linkToSession, changeOriginMsgMapRef, originMsgMapRef }
    ) => {
        let session_id = requestChatParams?.session_id;
        if (isNewSession) {
            // DeerFlow 新会话：session_id 设为空字符串，等待后端返回 thread_id
            session_id = '';
            currentSessionMapIdRef.current = '';
            // 暂时不跳转，等后端返回 thread_id 后再跳转

            requestChatParams.session_key = session_id;
            let curList = sessionListRef.current ? sessionListRef.current : [];
            // 仅在当前会话列表为空或首项为占位（'新会话'）时插入占位项，避免覆盖已有有意义的会话名称
            const needInsertPlaceholder =
                !Array.isArray(curList) || curList.length === 0 || !curList[0] || !curList[0].session_name || curList[0].session_name === '新会话';
            if (needInsertPlaceholder) {
                // 🔥 修复：添加 handler_json 字段，确保刷新后能正确识别 deerflow 会话
                let data = [
                    {
                        session_name: '新会话',
                        time_range: '1',
                        session_id: session_id,
                        skill_id: requestChatParams?.skill_id,
                        handler_json: ['deerflow'] // 添加 handler_json
                    },
                    ...curList
                ];
                formatSessionList(data);
            }
        }

        // 对于 DeerFlow 聊天的会话 ID 处理
        if (!requestChatParams.session_id || requestChatParams.session_id === 0) {
            // 新会话设置为空字符串，等待后端返回
            requestChatParams.session_id = '';
        }

        // 针对 DeerFlow 聊天，除了"修改计划"或"开始研究"外，不带 threadId
        if (!editPlanMode && requestChatParams.value !== 'start_research') {
            delete requestChatParams.threadId;
        }

        // 设置session status为0（表示正在加载中）
        const currentSessionId = currentSessionMapIdRef.current || session_id;

        // 在用户发送新消息前，尝试清除可能的用户停止标记，避免之前的 stop 导致新消息一直卡在 loading
        try {
            const possibleIds = [];
            if (requestChatParams && requestChatParams.threadId) {
                possibleIds.push(requestChatParams.threadId);
            }
            const existing = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[currentSessionId] || {} : {};
            const msgs = existing.messages || [];
            const lastTaskMsg = Array.isArray(msgs) && msgs.length ? [...msgs].reverse().find((m) => m && m.task_id) : null;
            if (lastTaskMsg && lastTaskMsg.task_id) {
                possibleIds.push(lastTaskMsg.task_id);
            }
            if (currentSessionId) possibleIds.push(currentSessionId);

            possibleIds.forEach((id) => {
                try {
                    if (id) clearStoppedState(id);
                } catch (e) {}
            });
        } catch (e) {
            // ignore
        }

        changeOriginMsgMapRef({
            [currentSessionId]: {
                ...originMsgMapRef.current[currentSessionId],
                status: 0
            }
        });
        return { session_id, currentSessionId };
    };

    // DeerFlow历史记录处理
    const handleDeerFlowHistoryWithUI = async (
        session,
        {
            checkIsLogin,
            formatHistoryRecord,
            changeSessionMapItem,
            changeOriginMsgMapRef,
            originMsgMapRef,
            startHistoryPolling,
            formatSessionList,
            linkToSession,
            currentSessionMapIdRef,
            skillListRef
        }
    ) => {
        // DeerFlow历史记录获取前检查登录状态
        const isLogin = await checkIsLogin();
        if (!isLogin) {
            return false;
        }

        const res = await apiHandleDeerFlowHistory({ thread_id: session.session_id });
        if (res && res.code === 200) {
            // 处理DeerFlow特有的历史记录格式
            let { showMsg, allMsg } = await formatHistoryRecord(res.data.messages, session);

            // 检查任务状态
            const taskStatusMap = res.data?.task_status_map || {};
            const currentTaskId = res.data?.current_task_id;
            const hasLoadingMessage = res.data.messages && res.data.messages.some((msg) => msg.status === 'loading');

            // 🔥 关键修复：获取task_status_map中的最后一个task_id
            const taskIds = Object.keys(taskStatusMap);
            const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;

            // 获取当前任务的具体状态
            const currentTaskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

            // 🔥 关键修复：使用最后一个task_id来检查停止状态
            const isUserStopped = lastTaskId ? checkUserStoppedState(lastTaskId) : false;

            // 🔥 关键修复：判断是否需要轮询的逻辑
            // 如果用户主动停止过，不需要轮询
            // 任务状态为2（继续）或有loading消息时需要轮询
            const needsPolling = !isUserStopped && (hasLoadingMessage || currentTaskStatus === 2);

            // 根据任务状态设置会话状态
            let sessionStatus = null;
            let finalTaskStatus = currentTaskStatus;

            // 🔥 如果用户主动停止过，将任务状态强制改为-1
            if (isUserStopped && lastTaskId) {
                finalTaskStatus = -1;
                sessionStatus = -1; // 🔥 用户主动停止，强制设为停止状态
            } else if (currentTaskStatus === -1) {
                sessionStatus = -1; // 任务停止
            } else if (currentTaskStatus === 0) {
                sessionStatus = null; // 任务完成
            } else if (currentTaskStatus === 2 || hasLoadingMessage) {
                sessionStatus = 0; // 任务进行中/轮询中
            }

            const sessionState = {
                messages: showMsg,
                loading: needsPolling,
                answering: false
            };

            changeSessionMapItem(session?.session_id, sessionState, allMsg);

            // 更新originMsgMapRef状态，正确区分完成、停止、进行中状态
            changeOriginMsgMapRef({
                [session?.session_id]: {
                    ...originMsgMapRef.current[session?.session_id],
                    messages: allMsg,
                    status: sessionStatus
                }
            });

            // 🔥 关键修复：如果需要轮询，启动轮询
            if (needsPolling && startHistoryPolling) {
                // 启动轮询，传递所有必要的参数
                startHistoryPolling(session, {
                    formatHistoryRecord,
                    changeSessionMapItem,
                    changeOriginMsgMapRef,
                    originMsgMapRef,
                    currentSessionMapIdRef,
                    skillListRef
                });
            }

            return true;
        }
        return false;
    };

    // 停止DeerFlow聊天
    const stopDeerFlow = (activeReportId, { currentSessionMapIdRef, originMsgMapRef, changeSessionMapItem, changeOriginMsgMapRef }) => {
        // 存储停止状态到localStorage
        if (activeReportId) {
            setUserStoppedState(activeReportId);
        }
        // 取消API请求
        cancelDeerFlowChat();

        // 调用后端停止API，优先使用 lastThreadIdRef；如果不存在则回退到 currentSessionId
        try {
            const fallbackThreadId = currentSessionMapIdRef && currentSessionMapIdRef.current ? currentSessionMapIdRef.current : null;
            const threadToStop = lastThreadIdRef.current || fallbackThreadId;
            if (threadToStop) {
                handleDeerFlowStop({ thread_id: threadToStop }).catch((error) => {
                    console.error('❌ handleDeerFlowStop调用失败:', error);
                });
            } else {
                console.warn('⚠️ 无可用 thread_id 用于停止 DeerFlow（lastThreadId 和 currentSessionId 都为空）');
            }
        } catch (err) {
            console.error('❌ 调用 handleDeerFlowStop 时发生异常:', err);
        }

        // 更新当前会话状态
        const currentSessionId = currentSessionMapIdRef.current;
        if (currentSessionId) {
            // 🔥 停止轮询，防止继续更新loading状态
            stopHistoryPolling(currentSessionId, true);

            // 🔥 清理轮询标记，防止handleDeerFlowChatWithUI继续轮询
            if (window.activeDeerFlowPollings) {
                window.activeDeerFlowPollings.delete(currentSessionId);
                // 如果有thread_id，也清除对应的标记
                if (lastThreadIdRef.current) {
                    window.activeDeerFlowPollings.delete(lastThreadIdRef.current);
                }
            }

            const currentOriginData = originMsgMapRef.current[currentSessionId];

            // 确保originMsgMapRef有正确的数组结构
            if (!Array.isArray(currentOriginData?.messages)) {
                originMsgMapRef.current[currentSessionId] = {
                    messages: currentOriginData?.messages || []
                };
            }

            // 标记最后一条AI消息为停止状态
            const messages = originMsgMapRef.current[currentSessionId].messages;
            if (messages && messages.length > 0) {
                const lastMessage = messages[messages.length - 1];
                if (lastMessage && lastMessage.role === 'ai') {
                    lastMessage.status = 'complete';
                    if (lastMessage.question && typeof lastMessage.question === 'object' && lastMessage.question !== null) {
                        lastMessage.question.status = 'stopped';
                    }
                }
            }

            changeSessionMapItem(currentSessionId, {
                loading: false,
                answering: false
            });

            // 设置session status为-1，表示已停止
            changeOriginMsgMapRef({
                [currentSessionId]: {
                    ...originMsgMapRef.current[currentSessionId],
                    status: -1
                }
            });

            // 清除会话的暂停标识
            clearSessionPauseFlag(currentSessionId);
        }

        // 使用传入的activeReportId，如果没有则使用lastThreadIdRef.current
        const reportIdToUse = activeReportId || lastThreadIdRef.current;

        // 派发停止事件给研究卡片
        window.dispatchEvent(
            new CustomEvent('report-state-change', {
                detail: {
                    reportId: reportIdToUse,
                    uniqueReportId: reportIdToUse,
                    status: 'stopped',
                    userStopped: true,
                    isOpen: false
                }
            })
        );

        // 派发关闭侧边栏事件
        window.dispatchEvent(
            new CustomEvent('close-report-sidebar', {
                detail: {
                    reportId: reportIdToUse
                }
            })
        );
    };

    // DeerFlow消息中的研究计划处理
    const handleDeerFlowPlanButton = (params, { onSubmitMsg, setEditPlanMode: setEditPlanModeCallback }) => {
        if (params.value === 'start_research') {
            // 如果是"开始研究"消息，设置会话的暂停标识
            if (params.session_id) {
                try {
                    const historyKey = 'grt_history_sessions';
                    const existingSessions = JSON.parse(localStorage.getItem(historyKey) || '[]');

                    // 查找对应的会话
                    const sessionIndex = existingSessions.findIndex((item) =>
                        typeof item === 'object' ? item.sessionId === params.session_id : item === params.session_id
                    );

                    if (sessionIndex !== -1) {
                        // 如果会话存在，添加暂停标识
                        if (typeof existingSessions[sessionIndex] === 'string') {
                            // 兼容旧格式，转换为对象格式
                            existingSessions[sessionIndex] = {
                                sessionId: existingSessions[sessionIndex],
                                update_time: new Date().toISOString(),
                                canPause: true,
                                isClickable: true // 🔥 修复：同时设置可点击状态
                            };
                        } else {
                            // 已经是对象格式，直接添加标识
                            existingSessions[sessionIndex].canPause = true;
                            existingSessions[sessionIndex].isClickable = true; // 🔥 修复：同时设置可点击状态
                        }

                        localStorage.setItem(historyKey, JSON.stringify(existingSessions));
                    } else {
                        // 如果会话不存在，创建新的会话记录并设置暂停标识
                        const formatTime = (date) => {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            const hours = String(date.getHours()).padStart(2, '0');
                            const minutes = String(date.getMinutes()).padStart(2, '0');
                            const seconds = String(date.getSeconds()).padStart(2, '0');
                            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                        };

                        const currentTime = formatTime(new Date());
                        existingSessions.push({
                            sessionId: params.session_id,
                            update_time: currentTime,
                            canPause: true,
                            isClickable: true // 🔥 修复：设置为可点击
                        });
                        localStorage.setItem(historyKey, JSON.stringify(existingSessions));
                    }
                } catch (error) {
                    console.error('设置会话暂停标识失败:', error);
                }
            }
        }

        // 发送消息时禁用所有计划按钮
        setPlanButtonsDisabled(true);

        // 添加修改计划模式的interrupt_feedback属性
        if (editPlanMode) {
            params.interrupt_feedback = 'edit_plan';
            params.value = params.value || 'edit_plan';
            // 发送完消息后自动退出修改计划模式
            if (setEditPlanModeCallback) {
                setEditPlanModeCallback(false);
            } else {
                setEditPlanMode(false);
            }
        }

        return params;
    };

    return {
        // 状态
        editPlanMode,
        setEditPlanMode,
        planButtonsDisabled,
        setPlanButtonsDisabled,
        lastThreadIdRef,

        // 方法
        isDeerFlowType,
        toggleEditPlanMode,
        resetEditPlanMode,
        handleDeerFlowMessageUpdate,
        createDeerFlowRequest,
        handleDeerFlowSessionSwitch,
        handleDeerFlowBeforeSubmit,
        handleDeerFlowHistoryWithUI,
        stopDeerFlow,
        handleDeerFlowPlanButton,

        // 轮询管理方法
        startHistoryPolling,
        stopHistoryPolling,
        clearAllPollingTimers,
        historyPollingTimers,
        delayedStartTimers,

        // API方法
        cancelDeerFlowChat,
        handleDeerFlowStop,

        // 数据处理方法
        formatHistoryData,

        // 数据修复方法
        fixStoppedReportsData,
        analyzeDeerflowStoppedReports,

        // 会话管理方法
        clearSessionPauseFlag,

        // 🔥 修改：滚动管理方法
        scrollToBottomWithAnimation,
        getScrollToBottomParams
    };
};

export async function handleDeerFlowChatWithUI({
    nextId,
    msgList,
    requestChatParams,
    editPlanMode,
    lastThreadIdRef,
    updateMsgListByCurmsg,
    handleDeerFlowChatStream,
    handleMessages,
    changeSessionMapItem,
    changeOriginMsgMapRef, // 🔥 添加originMsgMapRef更新函数
    originMsgMapRef, // 🔥 添加originMsgMapRef引用
    isInAnswerRef,
    currentSessionMapIdRef,
    isNewSession = false, // 默认为 false
    deerFlowApiCall, // 添加API调用函数参数
    requestSessionId, // 🔥 添加发起请求时的会话ID
    historyPollingTimers = null // 🔥 新增：轮询定时器管理器，用于避免重复轮询
}) {
    // 使用会话ID作为 threadId（新会话时为空，后续使用固定的thread_id）
    let threadId = null;

    // 使用会话ID作为 threadId
    if (requestChatParams.session_id) {
        threadId = requestChatParams.session_id;
    }

    // 构建参数
    const params = createDeerFlowParams({
        question: requestChatParams.question,
        threadId: threadId,
        writingStyle: 'social_media',
        requestChatParams: requestChatParams
    });
    // 处理interrupt_feedback属性
    if (requestChatParams.interrupt_feedback === 'edit_plan') {
        params.interrupt_feedback = 'edit_plan';
    }
    // start_research 按钮逻辑
    if (requestChatParams.value === 'start_research') {
        params.interrupt_feedback = 'accepted';
        params.messages = [{ role: 'user', content: '太酷了！让我们开始吧。' }];
    }
    // 普通 DeerFlow 消息流
    const aiMsg = msgList.filter((m) => m.role === 'ai' && m.temId === nextId)[0] || {
        role: 'ai',
        status: 'loading',
        temId: nextId,
        content: ''
    };

    try {
        // 🔥 DeerFlow开始时，设置session status为0（加载中）
        if (changeOriginMsgMapRef && originMsgMapRef) {
            const _existingOrigin = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
            changeOriginMsgMapRef({
                [requestChatParams.session_id]: {
                    ..._existingOrigin,
                    status: 0
                }
            });
        }

        const response = await handleDeerFlowChat(params);
        if (response && response.code === 200 && response.content) {
            // 非流式接口返回的content是thread_id
            const threadId = response.content;
            // 如果是start_research操作，创建报告卡片
            if (requestChatParams.value === 'start_research') {
                const reportAiMsg = msgList.filter((m) => m.role === 'ai').slice(-1)[0];

                if (reportAiMsg) {
                    // 🔥 新增：获取当前页面已存在的所有task_id，避免重复
                    const existingTaskIds = new Set();
                    const existingReportCards = document.querySelectorAll('[data-type="report-notice"][data-unique-report-id]');
                    existingReportCards.forEach((card) => {
                        const taskId = card.getAttribute('data-unique-report-id');
                        if (taskId) {
                            existingTaskIds.add(taskId);
                        }
                    });

                    // 🔥 修改：轮询获取新的不重复的task_id
                    let uniqueReportId = null;
                    let retryCount = 0;
                    const maxRetries = 10; // 最多重试10次
                    const retryDelay = 5000; // 每次重试间隔5秒

                    while (!uniqueReportId && retryCount < maxRetries) {
                        const res = await apiHandleDeerFlowHistory({ thread_id: threadId });
                        if (res && res.code === 200 && res.data && res.data.messages && Array.isArray(res.data.messages)) {
                            // 查找最新的task_begin消息获取真实的task_id
                            for (let i = res.data.messages.length - 1; i >= 0; i--) {
                                const msg = res.data.messages[i];
                                if (msg.name === 'task_begin' && msg.task_id) {
                                    // 🔥 关键修复：检查是否为新的task_id
                                    if (!existingTaskIds.has(msg.task_id)) {
                                        uniqueReportId = msg.task_id;
                                        break;
                                    }
                                }
                            }
                        }

                        // 如果没有找到新的task_id，等待后重试
                        if (!uniqueReportId) {
                            retryCount++;
                            if (retryCount < maxRetries) {
                                await new Promise((resolve) => setTimeout(resolve, retryDelay));
                            }
                        }
                    }

                    // 如果最终没有找到新的task_id，使用threadId作为备选方案
                    if (!uniqueReportId) {
                        uniqueReportId = threadId;
                    }

                    const cardContent = `<div data-type="report-notice" data-title="研究报告" data-state="opened" data-auto-open="true" data-unique-report-id="${uniqueReportId}"></div>`;

                    // 发送事件通知清空侧边栏内容，避免显示旧报告
                    const clearEvent = new CustomEvent('report-content-update', {
                        detail: {
                            threadId: threadId,
                            reportId: uniqueReportId,
                            uniqueReportId: uniqueReportId,
                            activityData: '',
                            reportData: '',
                            title: '研究报告',
                            isNew: true,
                            status: 'loading', // 添加状态字段，表示加载中
                            clearPrevious: true, // 标记需要清空之前的内容
                            reportAvailable: false // 标记报告内容不可用
                        }
                    });
                    window.dispatchEvent(clearEvent);

                    updateMsgListByCurmsg(
                        {
                            ...reportAiMsg,
                            role: 'ai',
                            status: 'complete',
                            temId: reportAiMsg.temId,
                            content: cardContent,
                            threadId: threadId,
                            reportId: uniqueReportId,
                            session_id: lastThreadIdRef.current,
                            isReportMessage: true
                        },
                        false,
                        msgList,
                        requestChatParams.session_key
                    );
                }
            }

            // 更新thread_id
            lastThreadIdRef.current = threadId;

            // DeerFlow 新会话：设置会话ID
            if (requestChatParams && (isNewSession || !requestChatParams.session_id)) {
                requestChatParams.session_id = threadId;
                // 保存新会话到历史记录
                saveNewSessionToHistory(threadId).catch((error) => {
                    console.error('保存新会话到历史记录失败:', error);
                });
            }

            const initialIsCurrentSession = !requestSessionId || currentSessionMapIdRef?.current === requestSessionId;
            if (requestChatParams.value !== 'start_research' && initialIsCurrentSession) {
                updateMsgListByCurmsg(
                    {
                        ...aiMsg,
                        content: '任务已启动，正在处理中...',
                        status: 'loading',
                        session_id: threadId
                    },
                    false,
                    msgList,
                    requestChatParams.session_id
                );
            }

            // 记录轮询开始时的消息数量，用于判断是否有新消息
            let initialMessageCount = 0;
            try {
                const initialResponse = await apiHandleDeerFlowHistory({ thread_id: threadId });
                if (initialResponse && initialResponse.code === 200 && initialResponse.data) {
                    const initialMessages = initialResponse.data.messages || initialResponse.data;
                    if (Array.isArray(initialMessages)) {
                        initialMessageCount = initialMessages.length;
                    }
                }
            } catch (error) {
                console.error('获取初始消息数量失败:', error);
            }
            // 开始轮询获取历史记录
            const pollHistory = async () => {
                try {
                    const isPaused = getSessionPauseFlag(requestChatParams.session_id);
                    if (isPaused === false) {
                        // canPause为false表示会话被停止
                        return; // 会话被暂停，停止轮询
                    }
                    // 🔥 检查当前会话是否仍然是发起请求时的会话
                    // 如果会话已切换，轮询继续但不更新当前显示的UI
                    const isCurrentSession = !requestSessionId || currentSessionMapIdRef?.current === requestSessionId;

                    // 🔥 新增：防重复调用标记
                    const historyResponse = await apiHandleDeerFlowHistory({ thread_id: threadId });
                    if (historyResponse && historyResponse.code === 200 && historyResponse.data) {
                        const messages = historyResponse.data.messages || historyResponse.data;
                        // 🔥 根据历史记录接口返回的status字段更新会话状态
                        if (historyResponse.data.status !== undefined && changeOriginMsgMapRef && originMsgMapRef) {
                            const _existingOrigin =
                                originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
                            changeOriginMsgMapRef({
                                [requestChatParams.session_id]: {
                                    ..._existingOrigin,
                                    status: historyResponse.data.status
                                }
                            });

                            // 🔥 如果任务已完成（status为-1或1），清除会话的暂停标识
                            if (historyResponse.data.status === -1 || historyResponse.data.status === 1) {
                                clearSessionPauseFlag(requestChatParams.session_id);
                            }
                        }

                        // 触发自定义事件，通知全局轮询管理器
                        if (historyResponse.data) {
                            window.dispatchEvent(
                                new CustomEvent('deerflow-history-update', {
                                    detail: {
                                        threadId: threadId,
                                        sessionId: requestChatParams.session_id,
                                        historyData: historyResponse.data
                                    }
                                })
                            );
                        }

                        // 确保messages是数组
                        if (Array.isArray(messages) && messages.length > 0) {
                            // 如果消息数量没有增加，说明还没有新的AI回复
                            if (messages.length <= initialMessageCount) {
                                // 没有新消息，继续轮询（不直接return）
                            } else {
                                // 获取新增的消息（轮询开始后的消息）
                                const newMessages = messages.slice(initialMessageCount);

                                // 在新增消息中查找AI回复
                                const newAiMessages = newMessages.filter((msg) => {
                                    return msg.role === 'assistant' && msg.content && msg.content.trim() !== '';
                                });

                                // 基于时间戳匹配当前请求对应的AI回复
                                // 取轮询开始后最新的AI消息作为当前请求的回复
                                const lastAiMessage = newAiMessages.length > 0 ? newAiMessages[newAiMessages.length - 1] : null;

                                // 判断这个AI消息是否是我们期待的那个（必须有内容且在轮询开始后）
                                const isExpectedMessage = lastAiMessage && lastAiMessage.content && lastAiMessage.content.trim() !== '';

                                if (isExpectedMessage) {
                                    // 如果是start_research操作，需要特殊处理
                                    if (requestChatParams.value === 'start_research') {
                                        // 对于研究报告，只有收到reporter消息才停止轮询
                                        const isReporterMessage = lastAiMessage.name === 'reporter';

                                        if (!isReporterMessage) {
                                            // 不停止轮询，继续等待reporter消息
                                            // 对于报告消息，不要更新content，只发送侧边栏更新事件
                                            // 因为更新content会覆盖报告卡片的标识，导致卡片变成纯文本

                                            // 发送内容更新事件，更新侧边栏显示
                                            const reportCardElements = document.querySelectorAll('[data-type="report-notice"]');
                                            const latestReportCard = Array.from(reportCardElements).slice(-1)[0];
                                            if (latestReportCard) {
                                                const uniqueReportId = latestReportCard.getAttribute('data-unique-report-id');
                                                if (uniqueReportId) {
                                                    // 根据消息类型发送不同的事件
                                                    if (lastAiMessage.name === 'researcher' || lastAiMessage.name === 'coder') {
                                                        // 发送研究过程内容更新事件
                                                        const activityUpdateEvent = new CustomEvent('activity-content-update', {
                                                            detail: {
                                                                uniqueReportId: uniqueReportId,
                                                                content: lastAiMessage.content,
                                                                status: 'running'
                                                            }
                                                        });
                                                        window.dispatchEvent(activityUpdateEvent);
                                                    } else if (lastAiMessage.name === 'reporter') {
                                                        // 发送报告内容更新事件
                                                        const reportUpdateEvent = new CustomEvent('report-content-update', {
                                                            detail: {
                                                                uniqueReportId: uniqueReportId,
                                                                content: lastAiMessage.content,
                                                                status: 'running'
                                                            }
                                                        });
                                                        window.dispatchEvent(reportUpdateEvent);
                                                    }
                                                }
                                            }

                                            // 继续轮询，不执行return
                                        } else {
                                            // reporter消息，停止轮询
                                            // 但同样不要更新content，避免覆盖报告卡片标识

                                            // 发送内容更新和状态更新事件
                                            const reportCardElements = document.querySelectorAll('[data-type="report-notice"]');
                                            const latestReportCard = Array.from(reportCardElements).slice(-1)[0]; // 获取最新的报告卡片
                                            if (latestReportCard) {
                                                const uniqueReportId = latestReportCard.getAttribute('data-unique-report-id');
                                                if (uniqueReportId) {
                                                    // 发送报告内容更新事件
                                                    const contentUpdateEvent = new CustomEvent('report-content-update', {
                                                        detail: {
                                                            uniqueReportId: uniqueReportId,
                                                            content: lastAiMessage.content,
                                                            status: 'success'
                                                        }
                                                    });
                                                    window.dispatchEvent(contentUpdateEvent);

                                                    // 发送报告状态更新事件（通知卡片更新状态）
                                                    const stateUpdateEvent = new CustomEvent('report-state-change', {
                                                        detail: {
                                                            reportId: uniqueReportId,
                                                            uniqueReportId: uniqueReportId,
                                                            status: 'success',
                                                            userStopped: false
                                                        }
                                                    });
                                                    window.dispatchEvent(stateUpdateEvent);
                                                }
                                            }

                                            // 🔥 清理轮询标记
                                            if (window.activeDeerFlowPollings) {
                                                window.activeDeerFlowPollings.delete(threadId);
                                            }

                                            // 🔥 轮询停止时，清除会话暂停标识
                                            clearSessionPauseFlag(requestChatParams.session_id);

                                            // 🔥 轮询停止时，通知WebSender隐藏暂停图标
                                            window.dispatchEvent(
                                                new CustomEvent('deerflow-task-completed', {
                                                    detail: {
                                                        sessionId: requestChatParams.session_id,
                                                        threadId: threadId,
                                                        reason: 'reporter_completed'
                                                    }
                                                })
                                            );
                                            return; // 只有reporter消息才停止轮询
                                        }
                                    } else {
                                        // 非start_research操作，正常更新消息

                                        updateMsgListByCurmsg(
                                            {
                                                ...aiMsg,
                                                content: lastAiMessage.content,
                                                status: 'complete',
                                                session_id: threadId
                                            },
                                            false,
                                            msgList,
                                            requestChatParams.session_id
                                        );
                                        // 🔥 清理轮询标记
                                        if (window.activeDeerFlowPollings) {
                                            window.activeDeerFlowPollings.delete(threadId);
                                        }

                                        // 🔥 轮询停止时，清除会话暂停标识
                                        clearSessionPauseFlag(requestChatParams.session_id);

                                        // 🔥 轮询停止时，通知WebSender隐藏暂停图标
                                        window.dispatchEvent(
                                            new CustomEvent('deerflow-task-completed', {
                                                detail: {
                                                    sessionId: requestChatParams.session_id,
                                                    threadId: threadId,
                                                    reason: 'task_completed'
                                                }
                                            })
                                        );
                                        return; // 非研究报告操作，停止轮询
                                    }
                                } else {
                                    // 尝试查找其他可能的AI消息格式（也要检查时间戳）
                                    const otherAiMessages = newMessages.filter((msg) => {
                                        if (msg.role === 'user' || msg.role === 'human' || !msg.content) {
                                            return false;
                                        }

                                        return true;
                                    });

                                    // 取最新的其他格式AI消息
                                    if (otherAiMessages.length > 0) {
                                        const lastOtherMessage = otherAiMessages[otherAiMessages.length - 1];

                                        // 如果是start_research操作，同样不要更新content
                                        if (requestChatParams.value === 'start_research') {
                                            // 对于报告消息，不更新content，避免覆盖报告卡片标识

                                            // 发送事件更新报告卡片状态为success
                                            const reportCardElements = document.querySelectorAll('[data-type="report-notice"]');
                                            const latestReportCard = Array.from(reportCardElements).slice(-1)[0];
                                            if (latestReportCard) {
                                                const uniqueReportId = latestReportCard.getAttribute('data-unique-report-id');
                                                if (uniqueReportId) {
                                                    // 🔥 修复：从报告内容中提取真实标题
                                                    let extractedTitle = '研究报告';
                                                    if (lastOtherMessage.content && typeof lastOtherMessage.content === 'string') {
                                                        const titleMatch = lastOtherMessage.content.match(/^#\s+(.+?)$/m);
                                                        if (titleMatch && titleMatch[1]) {
                                                            extractedTitle = titleMatch[1];
                                                        }
                                                    }

                                                    // 🔥 修复：确保报告完成时发送完整的内容和状态
                                                    const statusUpdateEvent = new CustomEvent('report-content-update', {
                                                        detail: {
                                                            uniqueReportId: uniqueReportId,
                                                            content: lastOtherMessage.content,
                                                            title: extractedTitle, // 🔥 使用提取的标题
                                                            status: 'success',
                                                            isFinal: true
                                                        }
                                                    });
                                                    window.dispatchEvent(statusUpdateEvent);

                                                    // 发送事件更新reportDataRef
                                                    const reportDataUpdateEvent = new CustomEvent('update-report-data-ref', {
                                                        detail: {
                                                            threadId: threadId,
                                                            reportId: uniqueReportId,
                                                            uniqueReportId: uniqueReportId,
                                                            activityData: '', // AI回复内容暂时作为活动数据
                                                            reportContent: lastOtherMessage.content,
                                                            title: extractedTitle, // 🔥 使用提取的标题
                                                            isFinal: true,
                                                            timestamp: Date.now()
                                                        }
                                                    });
                                                    window.dispatchEvent(reportDataUpdateEvent);
                                                }
                                            }
                                        } else {
                                            // 🔥 只有当前会话匹配时才更新UI
                                            if (isCurrentSession) {
                                                updateMsgListByCurmsg(
                                                    {
                                                        ...aiMsg,
                                                        content: lastOtherMessage.content,
                                                        status: 'complete',
                                                        session_id: threadId
                                                    },
                                                    false,
                                                    msgList,
                                                    requestChatParams.session_id
                                                );
                                            }
                                        }

                                        // 🔥 清理轮询标记
                                        if (window.activeDeerFlowPollings) {
                                            window.activeDeerFlowPollings.delete(threadId);
                                        }

                                        // 🔥 轮询停止时，清除会话暂停标识
                                        clearSessionPauseFlag(requestChatParams.session_id);

                                        // 🔥 轮询停止时，通知WebSender隐藏暂停图标
                                        window.dispatchEvent(
                                            new CustomEvent('deerflow-task-completed', {
                                                detail: {
                                                    sessionId: requestChatParams.session_id,
                                                    threadId: threadId,
                                                    reason: 'ai_message_completed'
                                                }
                                            })
                                        );
                                        return; // 停止轮询
                                    }
                                }
                            }
                        }
                    }

                    // 如果没有找到AI回复，继续轮询（最多轮询30次，每次间隔5秒）
                    if ((pollHistory.count || 0) < 70) {
                        pollHistory.count = (pollHistory.count || 0) + 1;

                        // 🔥 在继续轮询前再次检查会话是否切换
                        setTimeout(() => {
                            pollHistory();
                        }, 5000);
                    } else {
                        // 🔥 清理轮询标记
                        if (window.activeDeerFlowPollings) {
                            window.activeDeerFlowPollings.delete(threadId);
                        }

                        // 轮询超时，设置状态为1（任务完成）

                        if (changeOriginMsgMapRef && originMsgMapRef) {
                            const _existingOrigin =
                                originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
                            changeOriginMsgMapRef({
                                [requestChatParams.session_id]: {
                                    ..._existingOrigin,
                                    status: 1
                                }
                            });

                            // 🔥 清除会话的暂停标识
                            clearSessionPauseFlag(requestChatParams.session_id);
                        }

                        // 如果是start_research操作，不要替换报告卡片
                        // 🔥 只有当前会话匹配时才更新UI
                        const isCurrentSession = !requestSessionId || currentSessionMapIdRef?.current === requestSessionId;
                        if (requestChatParams.value !== 'start_research' && isCurrentSession) {
                            updateMsgListByCurmsg(
                                {
                                    ...aiMsg,
                                    content: '任务执行超时，请稍后手动刷新查看结果',
                                    status: 'error',
                                    session_id: threadId
                                },
                                false,
                                msgList,
                                requestChatParams.session_id
                            );
                        }
                    }
                } catch (error) {
                    console.error('轮询历史记录失败:', error);

                    // 轮询失败，继续尝试（最多3次失败后停止）
                    pollHistory.failCount = (pollHistory.failCount || 0) + 1;
                    if (pollHistory.failCount < 3) {
                        // 🔥 错误重试时继续轮询，但不检查会话切换（轮询继续，只是不更新UI）
                        setTimeout(() => {
                            pollHistory();
                        }, 5000);
                    } else {
                        // 🔥 清理轮询标记
                        if (window.activeDeerFlowPollings) {
                            window.activeDeerFlowPollings.delete(threadId);
                        }

                        // 轮询失败超过限制，设置状态为1（任务完成）

                        if (changeOriginMsgMapRef && originMsgMapRef) {
                            const _existingOrigin =
                                originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
                            changeOriginMsgMapRef({
                                [requestChatParams.session_id]: {
                                    ..._existingOrigin,
                                    status: 1
                                }
                            });

                            // 🔥 清除会话的暂停标识
                            clearSessionPauseFlag(requestChatParams.session_id);
                        }

                        // 如果是start_research操作，不要替换报告卡片
                        // 🔥 只有当前会话匹配时才更新UI
                        const isCurrentSession = !requestSessionId || currentSessionMapIdRef?.current === requestSessionId;
                        if (requestChatParams.value !== 'start_research' && isCurrentSession) {
                            updateMsgListByCurmsg(
                                {
                                    ...aiMsg,
                                    content: `获取任务结果失败：${error.message || '网络错误'}，请稍后手动刷新查看`,
                                    status: 'error',
                                    session_id: threadId
                                },
                                false,
                                msgList,
                                requestChatParams.session_id
                            );
                        }
                    }
                }
            };

            // 🔥 关键修复：检查是否已有startHistoryPolling轮询在运行
            if (historyPollingTimers && historyPollingTimers.current && historyPollingTimers.current.has(threadId)) {
                clearTimeout(historyPollingTimers.current.get(threadId));
                historyPollingTimers.current.delete(threadId);
            }

            // 🔥 检查是否已有handleDeerFlowChatWithUI轮询在运行
            if (!window.activeDeerFlowPollings) {
                window.activeDeerFlowPollings = new Set();
            }

            window.activeDeerFlowPollings.add(threadId);
            // 延迟2秒后开始第一次轮询（给任务一些执行时间）
            setTimeout(() => {
                pollHistory();
            }, 4000);
        } else {
            console.error('DeerFlow接口返回格式不正确:', response);
            throw new Error('接口返回格式不正确');
        }

        // 更新会话状态
        changeSessionMapItem(requestChatParams.session_id, { loading: false });

        // 🔥 DeerFlow结束时，设置session status为0
        if (changeOriginMsgMapRef && originMsgMapRef) {
            const _existingOrigin = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
            changeOriginMsgMapRef({
                [requestChatParams.session_id]: {
                    ..._existingOrigin,
                    status: 0
                }
            });
        }

        if (isInAnswerRef) isInAnswerRef.current = false;

        // 清理中断反馈标志
        if (requestChatParams.interrupt_feedback === 'edit_plan') {
            delete requestChatParams.interrupt_feedback;
        }
    } catch (error) {
        console.error('DeerFlow非流式请求失败:', error);

        // 错误处理
        changeSessionMapItem(requestChatParams.session_id, { loading: false });

        // 🔥 DeerFlow错误时，设置session status为0
        if (changeOriginMsgMapRef && originMsgMapRef) {
            const _existingOrigin = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[requestChatParams.session_id] || {} : {};
            changeOriginMsgMapRef({
                [requestChatParams.session_id]: {
                    ..._existingOrigin,
                    status: 0
                }
            });
        }
        if (isInAnswerRef) isInAnswerRef.current = false;

        // 更新消息状态为错误
        // 🔥 只有当前会话匹配时才更新UI
        const isCurrentSession = !requestSessionId || currentSessionMapIdRef?.current === requestSessionId;
        if (isCurrentSession) {
            updateMsgListByCurmsg(
                {
                    ...aiMsg,
                    content: aiMsg.content || '请求失败，请重试',
                    status: 'error',
                    session_id: requestChatParams.session_id
                },
                false,
                msgList,
                requestChatParams.session_id
            );
        }
    }
}

/**
 * 修复deerflow_stopped_reports中的错误数据
 * 将已完成的报告从停止列表中移除，只保留真正被用户停止的报告
 */
export const fixStoppedReportsData = () => {
    try {
        // 获取当前的停止报告列表
        const stoppedReports = JSON.parse(localStorage.getItem('deerflow_stopped_reports') || '{}');
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};

        const fixedStoppedReports = {};
        let removedCount = 0;

        // 遍历所有在 deerflow_stopped_reports 中的报告
        Object.keys(stoppedReports).forEach((reportId) => {
            let shouldKeepAsStopped = true;

            // 检查是否在已完成报告中
            if (completedReports[reportId]) {
                shouldKeepAsStopped = false;
                removedCount++;
            }

            // 检查单独存储的报告数据
            const reportKey = `grt_report_${reportId}`;
            try {
                const savedData = JSON.parse(localStorage.getItem(reportKey) || '{}');
                if (savedData.isFinal || (savedData.reportData && savedData.title && savedData.title !== '研究报告')) {
                    shouldKeepAsStopped = false;
                    removedCount++;
                }
            } catch (e) {
                // 忽略JSON解析错误
            }

            // 如果确定是真正被停止的报告，保留在停止列表中
            if (shouldKeepAsStopped) {
                fixedStoppedReports[reportId] = true;
            }
        });

        // 更新本地存储
        localStorage.setItem('deerflow_stopped_reports', JSON.stringify(fixedStoppedReports));

        return {
            success: true,
            removedCount,
            beforeFix: Object.keys(stoppedReports),
            afterFix: Object.keys(fixedStoppedReports)
        };
    } catch (error) {
        console.error('❌ 修复 deerflow_stopped_reports 数据时出错:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * 获取修复建议 - 分析当前数据状态
 */
export const analyzeDeerflowStoppedReports = () => {
    try {
        const stoppedReports = JSON.parse(localStorage.getItem('deerflow_stopped_reports') || '{}');
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};

        const analysis = {
            totalStopped: Object.keys(stoppedReports).length,
            potentiallyWronglyMarked: [],
            trulyStopped: [],
            recommendations: []
        };

        Object.keys(stoppedReports).forEach((reportId) => {
            let isWronglyMarked = false;
            let reasons = [];

            // 检查各种完成状态
            if (completedReports[reportId]) {
                isWronglyMarked = true;
                reasons.push('在已完成报告列表中');
            }

            const reportKey = `grt_report_${reportId}`;
            try {
                const savedData = JSON.parse(localStorage.getItem(reportKey) || '{}');
                if (savedData.isFinal) {
                    isWronglyMarked = true;
                    reasons.push('单独存储中标记为isFinal');
                }
            } catch (e) {
                // 忽略
            }

            if (isWronglyMarked) {
                analysis.potentiallyWronglyMarked.push({
                    reportId,
                    reasons
                });
            } else {
                analysis.trulyStopped.push(reportId);
            }
        });

        // 生成建议
        if (analysis.potentiallyWronglyMarked.length > 0) {
            analysis.recommendations.push(`发现 ${analysis.potentiallyWronglyMarked.length} 个可能被错误标记的报告`);
            analysis.recommendations.push('建议执行 fixStoppedReportsData() 函数进行修复');
        } else {
            analysis.recommendations.push('所有停止报告的标记都是正确的');
        }

        return analysis;
    } catch (error) {
        console.error('分析 deerflow_stopped_reports 时出错:', error);
        return { error: error.message };
    }
};

export default useDeerFlow;
// ==================== DeerFlow 工具函数和管理器 ====================

/**
 * 创建DeerFlow请求参数
 */
export const createDeerFlowParams = (options = {}) => {
    const { question, threadId, writingStyle = 'academic', requestChatParams } = options;

    // 基本请求参数
    const params = {
        messages: [{ role: 'user', content: question || 'Hello' }],
        thread_id: threadId && threadId !== '0' ? threadId : null,
        max_step_num: 3,
        max_search_results: 3,
        max_plan_iterations: 1,
        enable_background_investigation: false,
        auto_accepted_plan: false,
        report_style: writingStyle
    };

    // 添加 chat_info 参数
    if (requestChatParams) {
        params.chat_info = {
            user_id: requestChatParams.user_id,
            agent_id: requestChatParams.agent_id,
            skill_id: requestChatParams.skill_id
        };
    }

    return params;
};

/**
 * 历史会话管理工具函数
 */
export const saveNewSessionToHistory = async (sessionId) => {
    if (!sessionId) return;

    try {
        const historyKey = 'grt_history_sessions';
        const existingSessions = JSON.parse(localStorage.getItem(historyKey) || '[]');

        // 格式化当前时间为 YYYY-MM-DD HH:mm:ss
        const now = new Date();
        const formatTime = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const currentTime = formatTime(now);

        // 检查是否已存在该sessionId
        const existingIndex = existingSessions.findIndex((item) => (typeof item === 'object' ? item.sessionId === sessionId : item === sessionId));

        if (existingIndex === -1) {
            // 不存在则添加新的会话记录
            existingSessions.push({
                sessionId: sessionId,
                update_time: currentTime,
                canPause: true,
                isClickable: true // 🔥 修复：设置为可点击
            });
            localStorage.setItem(historyKey, JSON.stringify(existingSessions));

            // 新会话创建后调用历史记录接口
            try {
                await apiHandleDeerFlowHistory({ thread_id: sessionId });
            } catch (historyError) {
                console.error('调用历史记录接口失败:', historyError);
            }
        } else {
            // 存在则更新时间，但保留已有的canPause和isClickable字段
            const existingSession = existingSessions[existingIndex];
            const updatedSession = {
                sessionId: sessionId,
                update_time: currentTime
            };

            // 如果已有会话包含canPause字段，保留它
            if (typeof existingSession === 'object' && existingSession.canPause !== undefined) {
                updatedSession.canPause = existingSession.canPause;
            }

            // 🔥 修复：如果已有会话包含isClickable字段，保留它
            if (typeof existingSession === 'object' && existingSession.isClickable !== undefined) {
                updatedSession.isClickable = existingSession.isClickable;
            }

            existingSessions[existingIndex] = updatedSession;
            localStorage.setItem(historyKey, JSON.stringify(existingSessions));
        }
    } catch (error) {
        console.error('保存历史会话失败:', error);
    }
};

/**
 * 获取历史会话列表
 */
export const getHistorySessionsFromLocal = () => {
    try {
        const historyKey = 'grt_history_sessions';
        const sessions = JSON.parse(localStorage.getItem(historyKey) || '[]');

        // 兼容旧格式：如果是字符串数组，转换为对象数组
        return sessions.map((item) => {
            if (typeof item === 'string') {
                return {
                    sessionId: item,
                    update_time: '2025-01-01 00:00:00' // 给旧数据一个默认时间
                };
            }
            return item;
        });
    } catch (error) {
        console.error('获取历史会话失败:', error);
        return [];
    }
};

/**
 * 全局轮询管理器，避免重复轮询和管理轮询状态
 */
class HistoryPollingManager {
    constructor() {
        this.activePollings = new Map(); // threadId -> { promise, controller, attempts, startTime }
        this.cache = new Map(); // threadId -> { data, timestamp }
        this.CACHE_DURATION = 30000; // 缓存30秒
    }

    // 取消指定线程的轮询
    cancelPolling(threadId, taskId = null) {
        // 如果指定了taskId，尝试取消特定任务的轮询
        if (taskId) {
            const taskKey = `${threadId}:${taskId}`;
            const taskPolling = this.activePollings.get(taskKey);
            if (taskPolling) {
                taskPolling.controller.abort();
                this.activePollings.delete(taskKey);
                return;
            }
        }

        // 取消该线程的所有轮询（包括无taskId的轮询和所有有taskId的轮询）
        const keysToDelete = [];
        for (const [key, polling] of this.activePollings.entries()) {
            if (key === threadId || key.startsWith(`${threadId}:`)) {
                polling.controller.abort();
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach((key) => this.activePollings.delete(key));
    }

    // 检查缓存是否有效
    getCachedData(threadId) {
        const cached = this.cache.get(threadId);
        if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
            return cached.data;
        }
        return null;
    }

    // 设置缓存
    setCachedData(threadId, data) {
        this.cache.set(threadId, {
            data: data,
            timestamp: Date.now()
        });
    }

    // 智能轮询：检查是否已有进行中的轮询，避免重复
    async pollHistory(threadId, maxAttempts = 15, intervalMs = 2000, taskId = null) {
        // 创建复合键：如果有taskId，使用threadId+taskId作为键，否则只使用threadId
        const cacheKey = taskId ? `${threadId}:${taskId}` : threadId;

        // 首先检查缓存
        const cachedData = this.getCachedData(cacheKey);
        if (cachedData) {
            // 如果指定了taskId，检查缓存数据中是否包含该任务的消息
            if (taskId && cachedData.success && cachedData.data && cachedData.data.data && cachedData.data.data.messages) {
                const hasTaskMessages = cachedData.data.data.messages.some((msg) => msg.task_id === taskId);
                if (hasTaskMessages) {
                    return cachedData;
                }
            } else if (!taskId) {
                return cachedData;
            }
        }

        // 检查是否已有进行中的轮询 - 使用复合键
        const existingPolling = this.activePollings.get(cacheKey);
        if (existingPolling) {
            try {
                return await existingPolling.promise;
            } catch (error) {
                // 如果之前的轮询失败了，清理并开始新的轮询
                this.activePollings.delete(cacheKey);
            }
        }

        // 创建新的轮询
        const controller = new AbortController();
        let attempts = 0;

        const pollPromise = new Promise(async (resolve, reject) => {
            const poll = async () => {
                try {
                    // 检查是否被取消
                    if (controller.signal.aborted) {
                        reject(new Error('轮询被取消'));
                        return;
                    }

                    attempts++;
                    const response = await apiHandleDeerFlowHistory({ thread_id: threadId });

                    if (response && response.code === 200 && response.data) {
                        const messages = response.data.messages || response.data;

                        if (Array.isArray(messages) && messages.length > 0) {
                            // 如果指定了taskId，检查是否包含该任务的消息
                            if (taskId) {
                                const hasTaskMessages = messages.some((msg) => msg.task_id === taskId);
                                if (!hasTaskMessages) {
                                    // 继续轮询
                                    if (attempts < maxAttempts) {
                                        setTimeout(poll, intervalMs);
                                        return;
                                    } else {
                                        const result = { success: false, error: `未找到task_id ${taskId}的消息` };
                                        resolve(result);
                                        return;
                                    }
                                }
                            }

                            const result = { success: true, data: response };
                            // 缓存结果 - 使用复合键
                            this.setCachedData(cacheKey, result);
                            resolve(result);
                            return;
                        }
                    }

                    // 如果没有获取到数据且还没达到最大尝试次数，继续轮询
                    if (attempts < maxAttempts) {
                        setTimeout(poll, intervalMs);
                    } else {
                        resolve({ success: false, error: '轮询超时' });
                    }
                } catch (error) {
                    console.error(`轮询历史记录失败 - 第${attempts}次:`, error);

                    // 如果还没达到最大尝试次数，继续轮询
                    if (attempts < maxAttempts && !controller.signal.aborted) {
                        setTimeout(poll, intervalMs);
                    } else {
                        resolve({ success: false, error: error.message || '轮询失败' });
                    }
                }
            };

            // 开始轮询
            poll();
        });

        // 存储轮询信息 - 使用复合键
        this.activePollings.set(cacheKey, {
            promise: pollPromise,
            controller: controller,
            attempts: 0,
            startTime: Date.now()
        });

        // 轮询完成后清理
        pollPromise.finally(() => {
            this.activePollings.delete(cacheKey);
        });

        return pollPromise;
    }

    // 清理过期的缓存
    cleanExpiredCache() {
        const now = Date.now();
        for (const [threadId, cached] of this.cache.entries()) {
            if (now - cached.timestamp > this.CACHE_DURATION) {
                this.cache.delete(threadId);
            }
        }
    }
}

// 创建全局轮询管理器实例
const historyPollingManager = new HistoryPollingManager();

// 定期清理过期缓存
// 定期清理过期缓存（仅创建一次）
let historyPollingManagerCleanupInterval = null;
if (!historyPollingManagerCleanupInterval) {
    historyPollingManagerCleanupInterval = setInterval(() => {
        try {
            historyPollingManager.cleanExpiredCache();
        } catch (e) {
            console.error('清理历史轮询缓存失败:', e);
        }
    }, 60000); // 每分钟清理一次
}

// 提供停止清理定时器的接口，调用方可以在卸载或不再需要时停止定时器
export const stopHistoryPollingManagerCleanup = () => {
    if (historyPollingManagerCleanupInterval) {
        clearInterval(historyPollingManagerCleanupInterval);
        historyPollingManagerCleanupInterval = null;
    }
};

/**
 * 导出轮询函数，使用管理器
 */
export const pollHistoryForSidebar = async (threadId, maxAttempts = 15, intervalMs = 2000, taskId = null) => {
    return historyPollingManager.pollHistory(threadId, maxAttempts, intervalMs, taskId);
};

/**
 * 导出取消轮询的函数
 */
export const cancelHistoryPolling = (threadId, taskId = null) => {
    historyPollingManager.cancelPolling(threadId, taskId);
};

/**
 * DeerFlow 多会话轮询管理类
 */
class DeerFlowPollingManager {
    constructor() {
        // 使用Map来管理多个会话的轮询
        this.sessionPollings = new Map(); // sessionId -> { timerId, reportId, lastPollingTime }
        this.completedReportsRef = new Set();
    }

    // 清理指定会话的轮询
    clearSessionPolling(sessionId) {
        const polling = this.sessionPollings.get(sessionId);
        if (polling && polling.timerId) {
            clearInterval(polling.timerId);
            this.sessionPollings.delete(sessionId);
        }
    }

    // 清理所有轮询
    clearAllPolling() {
        for (const [sessionId, polling] of this.sessionPollings.entries()) {
            if (polling.timerId) {
                clearInterval(polling.timerId);
            }
        }
        this.sessionPollings.clear();
    }

    // 为指定会话设置轮询
    setSessionPolling(sessionId, reportId, callback, interval = 5000) {
        // 如果该会话已经有相同reportId的定时器在运行，直接返回
        const existingPolling = this.sessionPollings.get(sessionId);
        if (existingPolling && existingPolling.reportId === reportId && existingPolling.timerId) {
            return;
        }

        // 清理该会话的旧定时器
        this.clearSessionPolling(sessionId);

        // 设置新的定时器
        const timerId = setInterval(callback, interval);
        this.sessionPollings.set(sessionId, {
            timerId,
            reportId,
            lastPollingTime: 0
        });
    }

    // 兼容旧的API
    clearGlobalPolling() {
        this.clearAllPolling();
    }

    setGlobalPolling(reportId, callback, interval = 5000) {
        // 使用默认会话ID
        this.setSessionPolling('default', reportId, callback, interval);
    }

    // 从历史记录接口加载报告数据的函数（支持多会话）
    async loadReportFromHistoryGlobal(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc, sessionId = 'default') {
        if (!threadId) return;

        const currentReportId = uniqueReportId || reportId;

        // 检查报告是否已完成
        if (currentReportId && this.completedReportsRef.has(currentReportId)) {
            return;
        }

        // 按会话进行防抖机制
        const sessionPolling = this.sessionPollings.get(sessionId);
        if (sessionPolling) {
            const now = Date.now();
            const timeSinceLastCall = now - sessionPolling.lastPollingTime;
            if (timeSinceLastCall < 5000) {
                // 至少间隔5秒
                return;
            }
            sessionPolling.lastPollingTime = now;
        }

        try {
            const response = await apiHandleDeerFlowHistoryFunc({ thread_id: threadId });

            if (response && response.code === 200 && response.data && response.data.messages) {
                const messages = response.data.messages;
                const taskStatusMap = response.data.task_status_map;
                const currentTaskIdFromResponse = response.data.current_task_id;

                // 查找包含报告数据的消息
                let activityContent = '';
                let reportContent = '';
                let reportTitle = '研究报告';

                // 找到与当前reportId对应的任务
                let currentTaskId = null;
                let currentTaskStartIndex = -1;

                // 精确匹配 - 根据传入的reportId查找对应的任务
                if (reportId && uniqueReportId) {
                    for (let i = 0; i < messages.length; i++) {
                        const msg = messages[i];
                        if (msg.task_id === reportId || msg.task_id === uniqueReportId) {
                            // 找到匹配的消息，现在查找这个任务的开始位置
                            if ((msg.name === 'task_begin' || msg.role === 'task_begin') && msg.content) {
                                currentTaskId = msg.task_id;
                                currentTaskStartIndex = i;
                                reportTitle = msg.content;
                                break;
                            } else if (currentTaskId === null) {
                                // 记录找到的task_id，但继续寻找task_begin
                                currentTaskId = msg.task_id;
                                // 如果没有task_begin，从第一个匹配的消息开始
                                if (currentTaskStartIndex === -1) {
                                    currentTaskStartIndex = i;
                                }
                            }
                        }
                    }
                }

                // 如果精确匹配失败，查找最新的task_begin消息
                if (currentTaskStartIndex === -1) {
                    for (let i = messages.length - 1; i >= 0; i--) {
                        const msg = messages[i];
                        if ((msg.name === 'task_begin' || msg.role === 'task_begin') && msg.content) {
                            currentTaskId = msg.task_id;
                            currentTaskStartIndex = i;
                            reportTitle = msg.content;
                            break;
                        }
                    }
                }

                // 如果找到了任务开始，只处理该任务相关的消息
                if (currentTaskStartIndex >= 0 && currentTaskId) {
                    // 从任务开始位置往后处理消息
                    for (let i = currentTaskStartIndex; i < messages.length; i++) {
                        const msg = messages[i];
                        const messageType = msg.name || msg.role;

                        // 只处理属于当前任务的消息（支持researcher、coder和reporter）
                        if (msg.task_id === currentTaskId) {
                            if ((messageType === 'researcher' || messageType === 'coder') && msg.content) {
                                activityContent += msg.content + '\n\n';
                            } else if (messageType === 'reporter' && msg.content) {
                                reportContent = msg.content;
                            }
                        }
                    }
                }

                // 清理内容
                activityContent = activityContent.trim();
                reportContent = reportContent.trim();

                // 提取标题
                let extractedTitle = reportTitle;
                if (reportContent) {
                    const titleMatch = reportContent.match(/^#\s+(.+?)$/m);
                    if (titleMatch && titleMatch[1]) {
                        extractedTitle = titleMatch[1];
                    }
                }

                // 检查任务是否已完成（状态为0表示完成）
                let isTaskCompleted = false;
                if (taskStatusMap && currentTaskId) {
                    const taskStatus = taskStatusMap[currentTaskId];
                    isTaskCompleted = taskStatus === 0;
                }

                // 不再直接更新reportDataRef，而是通过事件通知
                if (activityContent || reportContent) {
                    // 只通过事件通知，让chat.jsx的handleUpdateReportData处理
                    const updateReportDataEvent = new CustomEvent('update-report-data', {
                        detail: {
                            threadId,
                            reportId: currentReportId,
                            uniqueReportId: currentReportId,
                            activityData: activityContent,
                            reportContent: reportContent,
                            title: extractedTitle,
                            isFinal: isTaskCompleted,
                            source: 'global-polling'
                        }
                    });
                    window.dispatchEvent(updateReportDataEvent);
                }

                // 只有在明确完成时才停止轮询
                if (isTaskCompleted) {
                    // 记录该报告已完成，避免重复轮询
                    if (currentReportId) {
                        this.completedReportsRef.add(currentReportId);
                        if (reportId && reportId !== currentReportId) {
                            this.completedReportsRef.add(reportId);
                        }
                        if (uniqueReportId && uniqueReportId !== currentReportId) {
                            this.completedReportsRef.add(uniqueReportId);
                        }
                    }

                    // 停止对应会话的轮询
                    this.clearSessionPolling(sessionId);

                    // 通知父组件更新状态
                    if (reportContent) {
                        const reportCompleteEvent = new CustomEvent('report-complete-from-history', {
                            detail: {
                                threadId,
                                reportId: currentReportId,
                                uniqueReportId: currentReportId,
                                title: extractedTitle,
                                isFinal: true,
                                status: 'success'
                            }
                        });

                        window.dispatchEvent(reportCompleteEvent);
                    }
                }
            }
        } catch (error) {
            console.error('全局轮询：从历史记录获取报告数据失败:', error);
        }
    }

    // 启动会话级别的轮询
    startSessionPolling(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc, sessionId = 'default') {
        if (!threadId) return;

        const currentReportId = uniqueReportId || reportId;

        // 强化的完成检查：检查多种ID组合
        const isReportCompleted =
            currentReportId &&
            (this.completedReportsRef.has(currentReportId) ||
                this.completedReportsRef.has(reportId) ||
                this.completedReportsRef.has(uniqueReportId) ||
                reportDataRef?.current?.isFinal);

        if (isReportCompleted) {
            return;
        }

        // 检查是否已经有相同reportId的会话级定时器在运行
        const existingPolling = this.sessionPollings.get(sessionId);
        if (existingPolling && existingPolling.reportId === currentReportId && existingPolling.timerId) {
            return;
        }

        // 立即执行一次，并检查是否已完成
        this.loadReportFromHistoryGlobal(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc, sessionId)
            .then(() => {
                // 如果在第一次调用后发现任务已完成，就不设置定时器了
                if (currentReportId && this.completedReportsRef.has(currentReportId)) {
                    return;
                }

                // 使用会话级定时器管理
                this.setSessionPolling(
                    sessionId,
                    currentReportId,
                    () => {
                        // 检查会话是否被暂停（用户点击了暂停按钮）
                        const isPaused = getSessionPauseFlag(sessionId);
                        if (isPaused === false) {
                            // canPause为false表示会话被停止
                            this.clearSessionPolling(sessionId);
                            return;
                        }

                        // 强化的完成检查：检查多种ID组合
                        const isReportCompleted =
                            currentReportId &&
                            (this.completedReportsRef.has(currentReportId) ||
                                this.completedReportsRef.has(reportId) ||
                                this.completedReportsRef.has(uniqueReportId) ||
                                reportDataRef?.current?.isFinal);

                        if (isReportCompleted) {
                            this.clearSessionPolling(sessionId);
                            return;
                        }
                        this.loadReportFromHistoryGlobal(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc, sessionId);
                    },
                    5000
                );
            })
            .catch((error) => {
                console.error(`会话 ${sessionId} 轮询：首次轮询失败:`, error);
            });
    }

    // 兼容旧的API
    startGlobalPolling(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc) {
        this.startSessionPolling(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc, 'default');
    }

    // 检查报告是否已完成（支持多ID检查）
    isReportCompleted(reportId, uniqueReportId = null) {
        if (!reportId) return false;

        // 检查所有可能的ID组合
        const idsToCheck = [reportId, uniqueReportId].filter(Boolean);
        return idsToCheck.some((id) => this.completedReportsRef.has(id));
    }

    // 标记报告为已完成（支持多ID标记）
    markReportCompleted(reportId, uniqueReportId = null) {
        const idsToMark = [reportId, uniqueReportId].filter(Boolean);
        idsToMark.forEach((id) => {
            this.completedReportsRef.add(id);
        });
    }

    // 重置管理器状态
    reset() {
        this.clearGlobalPolling();
        this.completedReportsRef.clear();
    }

    // 获取当前活跃的reportId（从会话轮询中获取）
    getActiveReportId() {
        // 优先返回默认会话的reportId
        const defaultPolling = this.sessionPollings.get('default');
        if (defaultPolling && defaultPolling.reportId) {
            return defaultPolling.reportId;
        }

        // 如果没有默认会话，返回任意一个活跃会话的reportId
        for (const [sessionId, polling] of this.sessionPollings.entries()) {
            if (polling && polling.reportId) {
                return polling.reportId;
            }
        }

        return null;
    }
}

// 创建全局DeerFlow轮询管理器实例
const deerFlowPollingManager = new DeerFlowPollingManager();

/**
 * DeerFlow 报告状态检查函数
 */
export const checkDeerFlowReportsStatus = async (originMsgMapRef, sessionId, setGeneratingReports, reportDataRef, triggerGlobalPollingCheck) => {
    try {
        // 获取当前会话的原始消息
        const currentSessionMessages = originMsgMapRef?.current?.[sessionId]?.messages || [];
        if (currentSessionMessages.length === 0) return;

        // 查找所有不同的 task_id
        const taskIds = new Set();
        currentSessionMessages.forEach((msg) => {
            if (
                msg.task_id &&
                (msg.name === 'researcher' || msg.name === 'coder' || msg.name === 'reporter' || msg.name === 'task_begin' || msg.name === 'human')
            ) {
                taskIds.add(msg.task_id);
            }
        });

        // 检查每个任务的完成状态
        const reportStatusUpdates = {};

        taskIds.forEach((taskId) => {
            // 检查是否有reporter消息（表示任务已完成）
            const hasReporterMessage = currentSessionMessages.some(
                (msg) => (msg.name === 'reporter' || msg.role === 'reporter') && msg.task_id === taskId
            );

            // 检查是否有用户消息（表示任务已开始）
            const hasUserMessage = currentSessionMessages.some((msg) => msg.name === 'human' && msg.task_id === taskId);

            // 找到相关的报告消息，获取正确的reportId
            const reportMessage = currentSessionMessages.find((msg) => msg.task_id === taskId && msg.question);

            let finalReportKey = taskId; // 默认使用taskId

            if (reportMessage) {
                // 提取uniqueReportId
                let uniqueReportId = null;
                if (typeof reportMessage.content === 'string') {
                    const match = reportMessage.content.match(/data-unique-report-id="([^"]+)"/);
                    if (match && match[1]) {
                        uniqueReportId = match[1];
                    }
                }

                // 检查question中的uniqueReportId
                if (!uniqueReportId && reportMessage.question && reportMessage.question.uniqueReportId) {
                    uniqueReportId = reportMessage.question.uniqueReportId;
                }

                // 生成reportId
                const reportId = reportMessage.question?.reportId;
                const finalReportId = reportId || uniqueReportId || reportMessage.id || reportMessage.sessionId;
                const finalUniqueReportId = uniqueReportId || finalReportId;

                finalReportKey = finalUniqueReportId;
            }

            if (hasReporterMessage) {
                // 只要存在reporter消息，就认为任务已完成（不管内容是否为空）
                reportStatusUpdates[finalReportKey] = false;
            } else if (hasUserMessage) {
                // 有用户消息但没有reporter消息，表示任务正在进行
                reportStatusUpdates[finalReportKey] = true;
            } else {
                reportStatusUpdates[finalReportKey] = true;
            }
        });

        // 批量更新状态
        if (Object.keys(reportStatusUpdates).length > 0) {
            setGeneratingReports((prev) => ({
                ...prev,
                ...reportStatusUpdates
            }));

            // 找到最新的正在进行的任务，为其创建reportDataRef条目
            const ongoingTasks = Object.entries(reportStatusUpdates).filter(([key, isGenerating]) => isGenerating);
            if (ongoingTasks.length > 0) {
                // 选择最后一个正在进行的任务（通常是最新的）
                const [latestTaskId] = ongoingTasks[ongoingTasks.length - 1];

                // 如果当前reportDataRef为空或者不是当前会话的，或者不是正在进行的任务
                if (
                    !reportDataRef.current.threadId ||
                    reportDataRef.current.threadId !== sessionId ||
                    !reportStatusUpdates[reportDataRef.current.reportId]
                ) {
                    reportDataRef.current = {
                        threadId: sessionId,
                        reportId: latestTaskId,
                        uniqueReportId: latestTaskId,
                        activityData: '',
                        reportContent: '',
                        title: '研究报告',
                        isFinal: false,
                        timestamp: Date.now()
                    };

                    // 触发全局轮询检查
                    triggerGlobalPollingCheck?.();
                }
            }
        }
    } catch (error) {
        console.error('检查报告状态失败:', error);
    }
};

/**
 * DeerFlow 本地存储清理函数
 */
export const cleanupDeerFlowLocalStorage = () => {
    try {
        // 清理过期的报告数据
        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
        const completedReports = unifiedData.completed || {};
        const generatingReports = unifiedData.generating || {};
        const now = Date.now();
        const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000; // 一周前
        let hasChanges = false;

        // 清理已完成的过期报告
        Object.keys(completedReports).forEach((key) => {
            if (completedReports[key] && completedReports[key].timestamp) {
                const timestamp = completedReports[key].timestamp;
                if (timestamp < oneWeekAgo) {
                    delete completedReports[key];
                    hasChanges = true;
                }
            }
        });

        // 清理生成中的过期报告
        Object.keys(generatingReports).forEach((key) => {
            if (generatingReports[key] && generatingReports[key].timestamp) {
                const timestamp = generatingReports[key].timestamp;
                if (timestamp < oneWeekAgo) {
                    delete generatingReports[key];
                    hasChanges = true;
                }
            }
        });

        if (hasChanges) {
            unifiedData.completed = completedReports;
            unifiedData.generating = generatingReports;
            localStorage.setItem('deerflow_reports_data', JSON.stringify(unifiedData));
        }

        // 清理旧格式的报告状态（兼容旧版本）
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('grt_report_state_report_')) {
                localStorage.removeItem(key);
            }
        }

        // 清理旧的本地存储
        localStorage.removeItem('grt_report_thread_map');
        localStorage.removeItem('last-report-id');
        localStorage.removeItem('last-report-thread-id');
    } catch (error) {
        console.error('清理本地存储失败', error);
    }
};

/**
 * 导出DeerFlow轮询管理器实例和相关方法
 */
export const deerFlowManager = deerFlowPollingManager;

// 兼容性导出函数
export const startDeerFlowGlobalPolling = (threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc) => {
    return deerFlowPollingManager.startGlobalPolling(threadId, reportId, uniqueReportId, reportDataRef, apiHandleDeerFlowHistoryFunc);
};

export const clearDeerFlowGlobalPolling = () => {
    return deerFlowPollingManager.clearGlobalPolling();
};

/**
 * 处理侧边栏打开的函数
 */
export const handleSidebarOpen = async ({
    threadId,
    reportId,
    uniqueReportId = null,
    sessionId,
    reportDataRef,
    triggerGlobalPollingCheck,
    setSidebarOpen,
    setActiveReportId,
    setActiveReportCardId,
    setUserManualClosed,
    setSidebarRenderKey,
    setReportData
}) => {
    // 使用uniqueReportId作为主要ID，如果不存在则使用reportId
    const effectiveReportId = uniqueReportId || reportId;

    // 立即清空旧数据，设置新报告的基础信息
    reportDataRef.current = {
        threadId: threadId || sessionId,
        reportId: effectiveReportId,
        uniqueReportId: uniqueReportId || effectiveReportId,
        activityData: '',
        reportContent: '',
        title: '研究报告',
        isFinal: false,
        timestamp: Date.now()
    };

    // 更新当前活跃的报告卡片ID，确保状态同步
    setActiveReportCardId(effectiveReportId);

    // 通过setReportData同步状态，确保侧边栏获得最新的reportDataRef数据
    if (setReportData) {
        setReportData({
            threadId: threadId || sessionId,
            reportId: effectiveReportId,
            uniqueReportId: uniqueReportId || effectiveReportId,
            activityData: '',
            reportContent: '',
            title: '研究报告',
            isFinal: false,
            timestamp: Date.now()
        });
    }

    // 立即设置侧边栏状态，避免异步操作导致的延迟
    setSidebarOpen(true);
    setUserManualClosed(false);

    // 在设置状态前，先加载报告数据并更新ref
    if (effectiveReportId) {
        // 尝试加载报告数据
        try {
            // 使用轮询方式获取完整的消息数据，确保数据完整性
            const pollResult = await pollHistoryForSidebar(threadId || sessionId, 15, 5000, effectiveReportId);
            let researcherMessages = [];
            let reporterMessages = [];

            // 获取任务状态，判断任务是否已完成
            let taskStatus = null;
            let isTaskCompleted = false;

            if (pollResult.success && pollResult.data && pollResult.data.code === 200 && pollResult.data.data && pollResult.data.data.messages) {
                const allMessages = pollResult.data.data.messages;
                taskStatus = pollResult.data.data.status; // 获取任务状态
                isTaskCompleted = taskStatus === 1; // 1表示已完成，0表示进行中

                // 查找与当前报告相关的researcher、coder和reporter消息
                researcherMessages = allMessages.filter(
                    (msg) => (msg.name === 'researcher' || msg.name === 'coder') && msg.task_id === effectiveReportId
                );
                reporterMessages = allMessages.filter((msg) => msg.name === 'reporter' && msg.task_id === effectiveReportId);
            }

            if (researcherMessages.length > 0 || reporterMessages.length > 0) {
                // 合并所有researcher消息作为activityData
                const activityData = researcherMessages.map((msg) => msg.content).join('\n\n---\n\n');
                // 使用最后一个reporter消息作为reportContent
                const reportContent = reporterMessages.length > 0 ? reporterMessages[reporterMessages.length - 1].content : '';

                // 从报告内容中提取标题
                let extractedTitle = '研究报告';
                if (reportContent) {
                    const titleMatch = reportContent.match(/^#\s+(.+?)$/m);
                    if (titleMatch && titleMatch[1]) {
                        extractedTitle = titleMatch[1];
                    }
                }

                // 直接更新ref中的数据
                reportDataRef.current = {
                    ...reportDataRef.current,
                    threadId: threadId || sessionId,
                    reportId: effectiveReportId,
                    uniqueReportId: uniqueReportId,
                    activityData: activityData,
                    reportContent: reportContent,
                    title: extractedTitle,
                    isFinal: isTaskCompleted, // 基于实际任务状态判断是否完成
                    taskStatus: taskStatus, // 添加原始任务状态
                    timestamp: Date.now()
                };

                // 同步更新setReportData，确保状态一致性
                if (setReportData) {
                    setReportData({
                        threadId: threadId || sessionId,
                        reportId: effectiveReportId,
                        uniqueReportId: uniqueReportId,
                        activityData: activityData,
                        reportContent: reportContent,
                        title: extractedTitle,
                        isFinal: isTaskCompleted,
                        taskStatus: taskStatus,
                        timestamp: Date.now()
                    });
                }

                // 如果是已完成的报告，立即将其添加到已完成列表中，防止被实时更新覆盖
                if (isTaskCompleted) {
                    // 通过自定义事件通知侧边栏组件将此报告标记为已完成
                    const markCompletedEvent = new CustomEvent('mark-report-completed', {
                        detail: {
                            reportId: effectiveReportId,
                            uniqueReportId: uniqueReportId,
                            reason: 'loaded_from_history'
                        }
                    });
                    window.dispatchEvent(markCompletedEvent);
                }

                // 手动触发全局轮询检查
                triggerGlobalPollingCheck?.();

                // 数据加载成功，现在可以安全设置activeReportId
                setActiveReportId(effectiveReportId);

                // 触发强制重渲染和数据更新
                setSidebarRenderKey((prev) => prev + 1);

                // 更新当前活动的报告数据
                setReportData({
                    threadId: threadId || sessionId,
                    reportId: effectiveReportId,
                    uniqueReportId: uniqueReportId,
                    activityData: activityData,
                    reportContent: reportContent,
                    title: extractedTitle,
                    isFinal: isTaskCompleted,
                    taskStatus: taskStatus
                });

                return; // 提前返回，不再尝试其他加载方法
            }

            // 如果无法从deerflow_reports加载，尝试使用传统方法
            const localStorageKey = `grt_report_${effectiveReportId}`;
            const savedData = localStorage.getItem(localStorageKey);

            if (savedData) {
                const parsedData = JSON.parse(savedData);

                // 从报告内容中提取标题
                let extractedTitle = parsedData.title || '研究报告';
                if (parsedData.reportContent && typeof parsedData.reportContent === 'string') {
                    const titleMatch = parsedData.reportContent.match(/^#\s+(.+?)$/m);
                    if (titleMatch && titleMatch[1]) {
                        extractedTitle = titleMatch[1];
                    }
                }

                // 直接更新ref中的数据
                reportDataRef.current = {
                    ...reportDataRef.current,
                    threadId,
                    reportId: effectiveReportId,
                    uniqueReportId: effectiveReportId,
                    activityData: parsedData.activityContent || '',
                    reportContent: parsedData.reportContent || '',
                    title: extractedTitle,
                    isFinal: parsedData.isFinal || false,
                    timestamp: parsedData.timestamp || Date.now()
                };

                // 如果是已完成的报告，标记为已完成防止被实时更新覆盖
                if (parsedData.isFinal) {
                    const markCompletedEvent = new CustomEvent('mark-report-completed', {
                        detail: {
                            reportId: effectiveReportId,
                            uniqueReportId: effectiveReportId,
                            reason: 'loaded_from_traditional_localStorage'
                        }
                    });
                    window.dispatchEvent(markCompletedEvent);
                }

                // 数据加载成功，现在可以安全设置activeReportId
                setActiveReportId(effectiveReportId);

                // 手动触发全局轮询检查
                triggerGlobalPollingCheck?.();
            } else {
                // 如果没有找到数据，确保reportDataRef有默认值
                reportDataRef.current = {
                    ...reportDataRef.current,
                    threadId,
                    reportId: effectiveReportId,
                    uniqueReportId: effectiveReportId,
                    activityData: reportDataRef.current.activityData || '',
                    reportContent: reportDataRef.current.reportContent || '',
                    title: reportDataRef.current.title || '研究报告',
                    isFinal: false,
                    timestamp: Date.now()
                };

                // 手动触发全局轮询检查
                triggerGlobalPollingCheck?.();
            }
        } catch (e) {
            // 出错时确保reportDataRef有默认值
            reportDataRef.current = {
                ...reportDataRef.current,
                threadId,
                reportId: effectiveReportId,
                uniqueReportId: effectiveReportId,
                activityData: '',
                reportContent: '',
                title: '研究报告',
                isFinal: false,
                timestamp: Date.now()
            };

            // 手动触发全局轮询检查
            triggerGlobalPollingCheck?.();
        }
    }

    // 确保activeReportId在最终也被设置（兜底）
    setActiveReportId(effectiveReportId);

    // 最终触发强制重渲染
    setSidebarRenderKey((prev) => prev + 1);

    // 保持正确的uniqueReportId
    setReportData({
        threadId,
        reportId: effectiveReportId,
        uniqueReportId: uniqueReportId || effectiveReportId,
        activityData: reportDataRef.current.activityData || '',
        reportContent: reportDataRef.current.reportContent || '',
        title: reportDataRef.current.title || '研究报告',
        isFinal: reportDataRef.current.isFinal || false
    });
};

// 工具函数：检查是否为文本类型
const isTextType = (content_type) => {
    if (
        content_type === contentType.annotation ||
        content_type === contentType.sensitive ||
        content_type === contentType.exception ||
        content_type === contentType.text ||
        content_type === contentType.retrieval ||
        content_type === contentType.markdown
    ) {
        return true;
    }
    return false;
};

// 工具函数：插入引用到内容中
function insertCitationToContent(original, citations) {
    if (!citations) {
        return original;
    }
    let result = original,
        citationList = [];
    // 按从后往前的顺序插入，避免索引变化
    citationList = citations.sort((a, b) => {
        if (a.end === b.end) {
            return b.index - a.index;
        }
        return b.end - a.end;
    });
    citationList.forEach((ele) => {
        result = `${result.slice(0, ele.end)} <citation type="${ele.type}"  id="${ele.id}">${ele.index}</citation> ${result.slice(ele.end)}`;
    });
    return result;
}
//处理历史数据
export const formatHistoryData = async (data, sessionId, isDeerFlow) => {
    const result = [];
    // 检查是否是DeerFlow的简单数据格式

    if (isDeerFlow && data && data.length > 0 && data[0].name && !data[0].section_id) {
        // DeerFlow简单格式处理
        data.forEach((item) => {
            // 特殊判断：如果内容以[edit_plan]开头，应该是用户输入
            let actualRole = item.role;
            let content = item.content;
            if (item.content && item.content.startsWith('[edit_plan]')) {
                actualRole = 'user';
                // 去掉[edit_plan]标签，只保留后面的文字内容
                content = item.content.replace(/^\[edit_plan\]\s*/, '');
            }

            const processedItem = {
                ...item,
                content: content,
                role: actualRole === 'user' ? 'local' : 'ai',
                status: item.status || 'complete' // 保留原始状态，如果没有状态则默认为complete
            };

            if (processedItem.role === 'ai') {
                // 不在这里包装样式，让后续渲染逻辑自动处理
                processedItem.content = content;
                processedItem.web_result = [];
                processedItem.retrieval_result = [];
                processedItem.answerContent = content;
                processedItem.thinkContent = '';

                // 检查是否是研究报告卡片消息（task_begin）
                if (item.name === 'task_begin') {
                    // 使用task_id作为报告ID，如果没有则生成一个
                    const uniqueReportId = item.task_id;

                    // 为研究报告卡片设置特殊的content格式
                    processedItem.content = `<div data-type="report-notice" data-title="${content}" data-state="closed" data-auto-open="false" data-unique-report-id="${uniqueReportId}"></div>`;
                    processedItem.isReportMessage = true;
                    processedItem.reportId = uniqueReportId;
                    processedItem.uniqueReportId = uniqueReportId;
                    processedItem.task_id = item.task_id; // 保存原始task_id
                }
                // 检查是否是研究过程消息（researcher或coder）
                else if (item.name === 'researcher' || item.name === 'coder') {
                    // 这些消息需要被隐藏，现在通过轮询接口获取数据
                    processedItem.isHidden = true;
                }
                // 检查是否是最终报告消息（reporter）
                else if (item.name === 'reporter') {
                    // 这些消息需要被隐藏，现在通过轮询接口获取数据
                    processedItem.isHidden = true;
                }
                // 检查是否是中断消息（包含选项按钮的消息）
                else if (
                    item.name === 'planner' &&
                    content &&
                    (content.includes('修改方案') ||
                        content.includes('开始研究') ||
                        content.includes('步骤') ||
                        content.includes('研究') ||
                        (content.includes('1.') && content.includes('2.') && content.includes('3.')))
                ) {
                    processedItem.options = [
                        { text: '修改计划', value: 'modify_plan' },
                        { text: '开始研究', value: 'start_research', primary: true }
                    ];
                }
            }

            result.push(processedItem);
        });

        // 修复：检测DeerFlow消息中的未回复用户消息
        // 找到最后一条用户消息
        for (let i = result.length - 1; i >= 0; i--) {
            const msg = result[i];
            if (msg.role === 'local') {
                // 检查这条用户消息之后是否有AI回复
                let hasAIReply = false;
                for (let j = i + 1; j < result.length; j++) {
                    if (result[j].role === 'ai' && !result[j].isHidden) {
                        hasAIReply = true;
                        break;
                    }
                }

                // 修复：如果没有AI回复，新增一条loading状态的AI消息，而不是修改用户消息状态
                if (!hasAIReply) {
                    // 新增一条AI消息，设置为loading状态
                    const loadingAIMessage = {
                        _id: `loading_${Date.now()}`, // 生成临时ID
                        role: 'ai',
                        status: 'loading',
                        content: '',
                        answerContent: '',
                        thinkContent: '',
                        web_result: [],
                        retrieval_result: [],
                        session_id: msg.session_id,
                        createTime: new Date().toISOString()
                    };

                    result.push(loadingAIMessage);
                }
                break; // 只检查最后一条用户消息
            }
        }

        // 🔥 关键修复：区分普通DeerFlow消息和报告消息
        // 检查当前会话是否包含报告消息（研究任务）
        const hasReportMessages = result.some(
            (msg) =>
                msg.isReportMessage ||
                (msg.content && msg.content.includes('data-type="report-notice"')) ||
                msg.name === 'task_begin' ||
                msg.name === 'researcher' ||
                msg.name === 'coder' ||
                msg.name === 'reporter' ||
                (msg.options && msg.options.some((opt) => opt.value === 'start_research'))
        );

        // 🔥 修复：只对普通DeerFlow消息（非报告任务）进行canPause状态更新
        // 报告任务的状态管理由useChat.js中基于实时API状态的逻辑处理
        if (!hasReportMessages) {
            // 找到最后一条用户消息
            let lastUserMessageIndex = -1;
            for (let i = result.length - 1; i >= 0; i--) {
                if (result[i].role === 'local') {
                    lastUserMessageIndex = i;
                    break;
                }
            }

            // 如果有用户消息，检查是否有AI回复
            if (lastUserMessageIndex !== -1) {
                // 检查最后一条用户消息之后是否有AI回复
                let hasAiReply = false;
                for (let i = lastUserMessageIndex + 1; i < result.length; i++) {
                    const msg = result[i];
                    // 如果有AI回复且不是隐藏消息，说明任务已完成
                    if (msg.role === 'ai' && !msg.isHidden && msg.content && msg.content.trim() !== '') {
                        hasAiReply = true;
                        break;
                    }
                }

                // 如果任务已完成（hasAiReply为true），需要更新canPause状态
                if (hasAiReply) {
                    // 使用传入的sessionId参数，如果没有则从result中获取
                    const currentSessionId = sessionId || (result.length > 0 ? result[0].session_id : null);

                    if (currentSessionId) {
                        const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
                        const sessionIndex = historySessions.findIndex((session) =>
                            typeof session === 'object' ? session.sessionId === currentSessionId : session === currentSessionId
                        );

                        if (sessionIndex >= 0 && typeof historySessions[sessionIndex] === 'object') {
                            // 只有在确认是普通DeerFlow任务时才设置canPause为false
                            if (historySessions[sessionIndex].canPause === true) {
                                historySessions[sessionIndex].canPause = false;
                                localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));

                                // 触发任务完成事件，通知WebSender隐藏暂停图标
                                setTimeout(() => {
                                    window.dispatchEvent(
                                        new CustomEvent('deerflow-task-completed', {
                                            detail: {
                                                sessionId: currentSessionId,
                                                threadId: currentSessionId,
                                                reason: 'simple_deerflow_task_completed'
                                            }
                                        })
                                    );
                                }, 100);
                            }
                        }
                    }
                }
            }
        }

        return result;
    }

    const sectionMap = {};
    // 第一遍：按section_id分组并处理AI消息
    data.forEach((item) => {
        let { content_type, section_id, role, question, message_type, metadata_json, content } = item;

        if (!sectionMap[section_id]) {
            sectionMap[section_id] = {
                user: {
                    role: 'local',
                    status: 'complete',
                    component: null //兼容以后content为对象的字段，同时保持之前content仅为text格式
                },
                ai: {
                    status: 'complete', // 初始默认状态
                    web_result: [],
                    retrieval_result: [],
                    answerContent: '',
                    thinkContent: '',
                    content: '',
                    ai_evaluation: null,
                    objContent: null //兼容以后content为对象的字段，同时保持之前content仅为text格式
                }
            };
        }

        const section = sectionMap[item.section_id];

        if (role === 'user') {
            if (message_type === messageType.question) {
                section.user = {
                    ...item,
                    ...section.user,
                    // 保留原始状态，如果item中有状态则优先使用，否则保持section.user的状态
                    status: item.status || section.user.status
                };
                //card类型的问题，content为对象，需要提取question字段
                if (content_type === contentType.card) {
                    section.user.content = item?.content.question;
                    section.user.component = item?.content?.component;
                }
            }
        } else if (role === 'assistant') {
            //联网搜索
            if (content_type === contentType.web_search) {
                try {
                    section.ai.web_result = JSON.parse(content);
                } catch (error) {
                    section.ai.web_result = [];
                }
            }
            //知识库
            if (content_type === contentType.retrieval) {
                try {
                    section.ai.retrieval_result = JSON.parse(content);
                } catch (error) {
                    section.ai.retrieval_result = [];
                }
            }
            //深度思考
            if (content_type === contentType.deep_think) {
                section.ai.thinkContent = content;
            }
            //课程推荐
            if (content_type === contentType.recommend_course) {
                const json = parse(content, Allow.ALL);
                section.ai.recommend_course_result = {
                    ...json,
                    loading: false
                };
            }
            //Ai评阅
            if (content_type === contentType.ai_evaluation_item) {
                if (!section.ai.ai_evaluation) {
                    section.ai.ai_evaluation = {
                        ai_evaluation_item: {},
                        ai_evaluation_result: {}
                    };
                }
                section.ai.ai_evaluation.ai_evaluation_item = JSON.parse(content);
            }
            //Ai评阅
            if (content_type === contentType.multimodal_learning) {
                section.ai.multimodal_learning_result = JSON.parse(content);
            }
            if (content_type === contentType.ai_evaluation_result) {
                if (!section.ai.ai_evaluation) {
                    section.ai.ai_evaluation = {
                        ai_evaluation_item: {},
                        ai_evaluation_result: {}
                    };
                }
                section.ai.ai_evaluation.ai_evaluation_result = JSON.parse(content);
            }
            //text 只代表内容的类型，不代表回答部分
            if (isTextType(content_type)) {
                if (metadata_json && metadata_json?.citation) {
                    section.ai.answerContent = insertCitationToContent(content, metadata_json?.citation.data);
                } else {
                    section.ai.answerContent = content;
                }
            }

            section.ai.question = question || section.user.content;
            //card类型的问题，content为对象，需要提取question字段
            if (content_type === contentType.card) {
                section.ai.content = '';
                section.ai.card_result = item.content;
            }
            //没有类型按回答处理
            if (!content_type) {
                section.ai.content = content;
            }
            section.ai = {
                ...item,
                ...section.ai,
                // 保留原始状态，如果item中有状态则优先使用，否则保持section.ai的状态
                status: item.status || section.ai.status
            };
        }
    });
    let values = Object.values(sectionMap);
    values.forEach((value) => {
        // 添加用户消息
        if (value.user) {
            result.push(value.user);
        }

        // 构建AI消息
        const aiMessage = {
            ...value.ai
        };

        // 添加web结果（如果有）
        if (value.ai.web_result) {
            aiMessage.web_result = value.ai.web_result;
        }

        // 添加retrieval结果（如果有）
        if (value.ai.retrieval_result) {
            aiMessage.retrieval_result = value.ai.retrieval_result;
        }

        if (value.ai.answerContent.length > 0) {
            aiMessage.content = `${value.ai.answerContent}`;
        }
        if (value.ai.thinkContent.length > 0) {
            aiMessage.content = `<think>\n${value.ai.thinkContent}\n</think>${aiMessage.content}`;
        }

        result.push(aiMessage);
    });
    // 🔥 修复：对于普通消息（非DeerFlow），检查并更新历史会话的canPause状态
    const hasLoadingOrAnsweringMessages = result.some((msg) => msg.status === 'loading' || msg.status === 'answering');

    if (!hasLoadingOrAnsweringMessages) {
        // 获取当前会话ID（从result中获取）
        const currentSessionId = sessionId || (result.length > 0 ? result[0].session_id : null);

        if (currentSessionId) {
            const historySessions = JSON.parse(localStorage.getItem('grt_history_sessions') || '[]');
            const sessionIndex = historySessions.findIndex((session) =>
                typeof session === 'object' ? session.sessionId === currentSessionId : session === currentSessionId
            );

            if (sessionIndex >= 0 && typeof historySessions[sessionIndex] === 'object') {
                // 对于普通消息，如果没有loading状态就设置canPause为false
                if (historySessions[sessionIndex].canPause === true) {
                    historySessions[sessionIndex].canPause = false;
                    localStorage.setItem('grt_history_sessions', JSON.stringify(historySessions));

                    // 触发任务完成事件，通知WebSender隐藏暂停图标
                    setTimeout(() => {
                        window.dispatchEvent(
                            new CustomEvent('deerflow-task-completed', {
                                detail: {
                                    sessionId: currentSessionId,
                                    threadId: currentSessionId,
                                    reason: 'normal_message_completed'
                                }
                            })
                        );
                    }, 100);
                }
            }
        }
    }
    return result;
    // return mockData2;
    // return result.filter((item) => item._id);
};

// 导出工具函数
export { clearSessionPauseFlag, getSessionPauseFlag };
